import React, { useState,useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams, useLocation  } from "react-router-dom";

// ** MUI Components
import {
  IconButton,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Menu,
  MenuItem,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  Backdrop,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import { styled, useTheme } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Tab from "@mui/material/Tab";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineContent from "@mui/lab/TimelineContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import MuiTimeline from "@mui/lab/Timeline";
import ListItem from "@mui/material/ListItem";
import TimelineItem from "@mui/lab/TimelineItem";
import List from "@mui/material/List";
import { DataGrid } from '@mui/x-data-grid'
import { DataGridPro } from '@mui/x-data-grid-pro';
import Collapse from '@mui/material/Collapse'
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { getCounties } from "components/USTimezone_county";
// ** Custom Component Import
import CustomTextField from "../../components/mui/text-field";
import CustomChip from "../../components/mui/chip";
import CustomAvatar from "../../components/mui/avatar";
import OptionsMenu from "../../components/option-menu";
import CustomAutocomplete from "../../components/mui/autocomplete";
import { useUnsavedChanges } from "../../components/UnsavedChangesContext";
import { saveAs } from 'file-saver';
// ** Icon Imports
import Icon from "../../components/icon";
import InfoIcon from "@mui/icons-material/Info";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import VideoPlaceholderIcon from 'images/video_placeholder.png';
import CSV_PlaceholderIcon from 'images/Attachment_Icons/CSV__PlaceholderIcon.png';
import PDF_PlaceholderIcon from 'images/Attachment_Icons/PDF_PlaceholderIcon.png';
import DOCX_PlaceholderIcon from 'images/Attachment_Icons/DOCX__PlaceholderIcon.png';
import XLSX_PlaceholderIcon from 'images/Attachment_Icons/XLSX__PlaceholderIcon.png';
import FilePlaceholder from 'images/image_placeholder.png';

// ** Third Party Components
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDropzone } from 'react-dropzone'

// ** Hook Import
import { useSettings } from "../../components/hooks/useSettings";

// ** Utils Import
import { hexToRGBA } from "../../components/utils/hex-to-rgba";

// ** Layout Import
import UserLayout from "../../components/layouts/UserLayout";
import { GetCustomer } from "./logic/GetCustomer";
import { GetFormatedTimeStamp } from "../../components/formatDate";

// ** Popup Dialog Component Imports
import AddContactDialog from './component/AddContact'
import UpdateContactDialog from './component/UpdateContactDialog'
import AddJobsiteDialog from './component/AddJobsite'
import DeactivateCustomerDialog from './component/DeactivateCustomer'
import AddCommunicationLogDialog from "./component/AddCommunicationLog";
import AttachmentUpload from "../../components/AttachmentUpload";
import SendPortalInviteDialog from './component/SendPortalInvite'
import DateRangeCalendar from './component/DateRangeCalendar';
import { stateNames ,expandStateName } from "../../components/US_StatesNames";
import { GetContacts } from "./logic/GetContacts";
import { getTimeZone, timezones } from '../../components/US_Timezones'
import NotesComponent from "screens/Requests/component/NotesComponent";
import PhoneInput from 'react-phone-input-2';
import { format } from 'date-fns';

import NProgress from "react-nprogress";
import FormHelperText from '@mui/material/FormHelperText';
import _ from 'lodash';
import { SaveCustomer } from "./logic/SaveCustomer";
import { SaveIndividual } from "./logic/SaveIndividual";
import { GetJobsite } from "./logic/GetJobsites";
import { DeleteJobsite } from "./logic/DeleteJobsite";
import QuickView from "./component/QuickView";
import { SavePropertyArea } from "./logic/SavePropertyArea";
import { GetPropertyArea } from "./logic/GetPropertyArea";
import { GetAlerts } from "./logic/GetAlerts";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { getToken } from "../../utils/auth";

import ClearIcon from "@mui/icons-material/Clear";
import BulkImport from "../../components/BulkImport";
import { DownloadAttachments } from "components/logic/DownloadAttachments";
import { useCustomerLogic } from './logic/CustomerLogic';
import { useIndexLogic } from "./logic/indexLogic"
import BillingInformation from "./component/BillingInformation";
import UnsavedChangesDialog from "components/UnsavedChangesDialog";
import { useBillingInfoPermission } from "./permissions/BillingInfoPermissions";
import { useContactPermission } from "./permissions/ContactPermissions";
import { useJobsitePermission } from "./permissions/JobsitePermissions";
import { useCustomerPermission } from "./permissions/CustomerPermissions";
import { useCommunicationLogPermission } from "./permissions/CommunicationLogPermissions";
import ImportExportOptions from "components/ImportExportOptions";
import { dev_baseUrl } from "utils/var";
import AddEmployeeDoodle from "images/AddEmployeeDoodle.png";

// Styled Timeline component
const Timeline = styled(MuiTimeline)({
  "& .MuiTimelineItem-root": {
    width: "100%",
    "&:before": {
      display: "none",
    },
  },
});

// ** Styled Components
const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : theme.palette.common.white,
    minWidth: 220,
    maxWidth: 440,
    border: "1px solid #E1E4EA",
  },
}));

const CustomChipWithDropdown = ({ tag, onSelect }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (selectedTag) => {
    onSelect(selectedTag);
    handleClose();
  };

  return (
    <Box>
      <CustomChip
        rounded
        size="small"
        skin="light"
        sx={{ lineHeight: 1,  "&:hover": { color: '#fff' } }}
        color={tag === "Active" ? "success" : "secondary"}
        icon={<FiberManualRecordIcon fontSize="small" />}
        label={tag}
        onClick={handleClick}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ sx: { width: 200 } }}
      >
        <MenuItem onClick={() => handleSelect(true)}>Active</MenuItem>
        <MenuItem onClick={() => handleSelect(false)}>Inactive</MenuItem>
      </Menu>
    </Box>
  );
};

  const columns = [
      {
        flex: 0.2,
        field: 'notificationDate',
        minWidth: 80,
        headerName: 'Notification Date'
      },
      {
        flex: 0.2,
        field: 'notificationType',
        minWidth: 80,
        headerName: 'Notification Type'
      },
      {
        flex: 0.4,
        field: 'notificationMsg',
        minWidth: 80,
        headerName: 'Notification Message'
      },
      {
        flex: 0.2,
        field: 'activityDate',
        minWidth: 80,
        headerName: 'Activity Date'
      },
  ];

  const columnsHistory = [
    {
      flex: 0.1,
      field: "id",
      minWidth: 120,
      headerName: "ID",
      renderCell: ({ row }) => {
        const { id } = row;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography 
            sx={{ 
              flexGrow: 1, 
              cursor: 'pointer', 
              color:'#007AFF',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}>
              {id}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.15,
      field: "type",
      minWidth: 140,
      headerName: "Type",
      renderCell: ({ row }) => {
        const { type } = row;
  
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography noWrap sx={{ml: 2}}>
              {type}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.15,
      field: "jobsite",
      minWidth: 150,
      headerName: "Jobsite",
      renderCell: ({ row }) => {
        const { jobsiteTitle, jobsiteAddress  } = row;
  
        return (
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: 'center', height: "100%" }}>
            <Typography noWrap>
              {jobsiteTitle}
            </Typography>
            <Typography variant='caption' noWrap>
              {jobsiteAddress}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.25,
      sortable: true,
      field: "timestamp",
      minWidth: 180,
      headerName: "Timestamp",
      renderCell: ({ row }) => {
        const { timestamp } = row;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap>
              {timestamp}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      sortable: true,
      field: "status",
      minWidth: 140,
      headerName: "Status",
      renderCell: ({ row }) => {
        const { status } = row;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <CustomChip 
              label={status}
              skin='light'
              color={ status === 'Active' ? 'success' : 'secondary' }
            />
          </Box>
        );
      },
    },
  ];

  const dummyData = [
    {
        id: 1,
        notificationDate: '2022-01-01',
        notificationType: 'Type 1',
        notificationMsg: 'Message 1',
        activityDate: '2022-01-01'
    }

  ];

  const dummyDataHistory = [
    {
      id: 'REQ001',
      type: 'Request',
      jobsiteTitle: 'Riverbend Residence',
      jobsiteAddress: '1234 Maple Street, Columbus, OH 43201',
      timestamp: '08/11/2024 3:33 PM',
      status: 'Active', 
    }
  ]

const CustomerDetail = () => {
  //#region Permissions
  const { PermissionProps } = useCustomerPermission();
  const {
    hasGetPermission
  } = PermissionProps;

  const { BillingPermissionProps } = useBillingInfoPermission();
  const { 
    hasBillingSavePermission,
    hasBillingGetPermission,
  } = BillingPermissionProps;

  const { ContactPermissionProps } = useContactPermission();
  const {
    hasContactSavePermission,
    hasContactGetPermission,
    hasContactDeletePermission,
  } = ContactPermissionProps;

  const { JobsitePermissionProps } = useJobsitePermission();
  const {
    hasJobsiteSavePermission,
    hasJobsiteGetPermission,
    hasJobsiteDeletePermission,
    hasJobsiteImportPermission,
  } = JobsitePermissionProps;

  const { CommunicationPermissionProps } = useCommunicationLogPermission();
  const {
    hasCommunicationSavePermission,
    hasCommunicationGetPermission,
    hasCommunicationDeletePermission,
  } = CommunicationPermissionProps;
  //#endregion Permissions

  // ** Hooks
  const { settings } = useSettings();
  const hiddenMD = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const { direction } = settings;
  const theme = useTheme();
  const contentHeightFixed = false;
  const smAbove = useMediaQuery(theme.breakpoints.up("sm"));
  const smBelow = useMediaQuery(theme.breakpoints.down("sm"));

  const { setHasUnsavedChanges, hasUnsavedChanges } = useUnsavedChanges();
  const [openUnsavedChangesDlg, setOpenUnsavedChangesDlg] = useState(false);
  const [unsavedDialogAction, setUnsavedDialogAction] = useState({
    action: '',
    tabValue: '',
  });

  const handleOpenUnsavedChangesDlg = (action, tabValue) => {
    if(action === 'tabChange'){
      setUnsavedDialogAction({
        action: action,
        tabValue: tabValue,
      });
    }else{
      setUnsavedDialogAction({
        action: action,
        tabValue: '',
      });
    }
    setOpenUnsavedChangesDlg(true); 
  }
  const handleCloseUnsavedChangesDlg = () => setOpenUnsavedChangesDlg(false);
  const handleDiscardChanges = () => { 
    if(unsavedDialogAction.action === 'tabChange'){
      setOpenUnsavedChangesDlg(false); 
      setTabValue(unsavedDialogAction.tabValue)

    }else if(unsavedDialogAction.action === 'upload'){
      setOpenUnsavedChangesDlg(false); 
      setFiles([]);
        setUploadFiles([]);
        setOpenUploadDlg(false); 
    }
    else{
      setOpenUnsavedChangesDlg(false); 
      navigate('/customers')
    }
  }

  // ** States
  const [openNewContactDlg, setOpenNewContactDlg] = useState(false);
  const [openUpdateContactDlg, setUpdateContactDlg] = useState(false);
  const [dataAddContact, setDataAddContact] = useState([]);
  const [paginationModelAddContact, setPaginationModelAddContact] = useState({
    page: 0,
    pageSize: 5,
  });
  const [paginationModel, setPaginationModel] = useState({
    pageNo: 1, // Default to page 1
    recordsPerPage: 10000, // Fetch all records at once
  });
  const [paginationModelHistory, setPaginationModelHistory] = useState({
    page: 0,
    pageSize: 5,
  });
  const [phoneFields, setPhoneFields] = useState([
    { phoneNo: "", isPrimary: true },
  ]);
  const { customerID } = useParams(); 
  const [customer, setCustomer] = useState({
    createDate: '',
    createBy: 0,
    modifiedDate: null,
    modifiedBy: null,
    isActive: true,
    customerID: 0,
    customerType: '',
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    isLead: false,
    referredBy: '',
    companyAddress_Street1: '',
    companyAddress_Street2: '',
    companyAddress_FloorLevel: '',
    companyAddress_UnitNumber: '',
    companyAddress_City: '',
    companyAddress_State: '',
    companyAddress_ZipCode: '',
    companyAddress_County: '',
    billingAddress: false,
    timeZone: '',
    clientCompanyID: 0,
    userID: 0,
    phoneNumbersJSON: null,
    phoneNumbers: phoneFields,
    createdBy: '',
    tags: null,
  });
  
  const [OldCustomer, SetOldCustomer] = useState({});
  const [tabValue, setTabValue] = useState("1");
  const [ddlArray, setDdlArray] = useState([]);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [jobsiteIsEmpty, setJobsiteIsEmpty] = useState(false);
  const [customerPortalIsEmpty, setCustomerPortalIsEmpty] = useState(true);
  const [loading, setLoading] = useState(true);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [openNewJobsiteDlg, setOpenNewJobsiteDlg] = useState(false);
  const [openNewComLogDlg, setOpenNewComLogDlg] = useState(false);
  const [comLogDialogDate, setComLogDialogDate] = useState(new Date());
  const [openDeactiveCustomerDlg, setOpenDeactiveCustomerDlg] = useState(false);
  const [openUploadDlg, setOpenUploadDlg] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [files, setFiles] = useState([])
  const [btnLoader, setBtnLoader] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(false);
  const [groupedAttachments, setGroupedAttachments] = useState({});
  const [contactID, setContactID] = useState(0);
  const [notesSort, setNotesSort] = useState(true);  
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMarkingPrimary, setIsMarkingPrimary] = useState(false);
  
  const [isChanged, setIsChanged] = useState(false);
  const [btnLoading, setButtonLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null); 
  const [totalRecords, setTotalRecords] = useState(null);   
  const [imageURL, setImageURL] = useState(null);
  const [thumbnails, setThumbnails] = useState({});
  const [globalContactsAll, setGlobalContactsAll] = useState([]);
  const [emailIndex, setEmailIndex] = useState(new Set());
  const [phoneIndex, setPhoneIndex] = useState(new Set());
  const [stateInput, setStateInput] = useState("");

  const {
    IndexProps
  } = useIndexLogic();

  const steps = [
    {
      icon: "tabler:user",
      title: "Profile Information",
    },
    {
      icon: "tabler:align-box-left-stretch",
      title: "Billing Information",
    },
    {
      icon: "tabler:address-book",
      title: "Add Contact",
    },
    {
      icon: "tabler:align-box-bottom-center",
      title: "Add Jobsite",
    },
  ];

  const [activeStep, setActiveStep] = useState(0);

  const { 
    setOpenNewCustomerDlg,
    // fetchData,
    allCustomers, 
  } = IndexProps;

  const {
    CustomerProps
  } = useCustomerLogic({activeStep, setActiveStep, steps, setOpenNewCustomerDlg,
    // fetchData, 
    allCustomers
  });

    const {
      GetBillingInfo,
      // Billing Info
      // estTravelTime, 
      // setEstTravelTime,
      billingFreq, 
      setBillingFreq,
      recurringType, 
      setRecurringType,
      depositReq, 
      setDepositReq,
      depositType, 
      setDepositType,
      formatsBilling, 
      setFormatsBilling,
      monthlyOptions, 
      setMonthlyOptions,
      // occurrences, 
      // setOccurrences,
      cycleStartDate, 
      setCycleStartDate,
      intervalDaysInput, 
      setIntervalDaysInput,
      weekOfMonth, 
      setWeekOfMonth,
      quarter, 
      setQuarter,
      monthOfQuarter, 
      setMonthOfQuarter,
      // handleEstTravelTimeChange,
      handleBillingFreqChange,
      handleRecurringTypeChange,
      handledDepositReqChange,
      handleDepositTypeChange,
      handleFormatBilling,
      formatsBillingWeekly,
      handleSaveBillingInfo,
      allEmployees,          
    } = CustomerProps;


  const initialJobsiteState = {
    jobsiteID: 0,
    customerID: customerID,              
    jobsiteType: 'Residential',
    jobsiteName: '',
    street1:  null,
    street2: null,
    floorLevel: null,
    unitNumber: null,
    city: null,
    state: null,
    zipCode: null,
    county: null,
    billingAddress: false,
    contact: null, 
  }

  const [jobsite, setJobsite] = useState(initialJobsiteState);
  const [Oldjobsite, setOldJobsite] = useState();

  const [allJobsites, setAllJobsites] = useState([]);

  const initialContact = {
    contactID: 0,
    firstName: null,
    lastName: null,
    email: null,
    jobTitle: null,
    customerID: 0,
    isPrimary: null,
    phoneNumbers:[],
    status:'active'
  }
  
  const [contact, setContact] = useState(initialContact);
  const [allContacts, setAllContacts] = useState([]);  

  const InitialpropertyArea = {
      propertyAreaID: 0,
      jobsiteID: 0,
      propertyAreaType: 'Indoor',
      propertyAreaName: null,
      description: null,
      measurement:null,
    }
    const [propertyArea, setPropertyArea] = useState(InitialpropertyArea);
    const [areaMeasurement, setAreaMeasurement] = useState({
      measurementID: 0,
      measurementName:null, 
      unitOfMeasure:null,
      length:0,
      width:0,
      height:0,
      total:0,
    });
  const [propertyAreaList, setPropertyAreaList] = useState([]);
  const [alertList, setAlertList] = useState([]);

  const [phoneError, setPhoneError] = useState({});
  const [errors, setErrors] = useState({});
  
  const [activeStepJobsite, setActiveStepJobsite] = useState(0);
  const [isValidPhone , setIsValidPhone] = useState({0:true});

  const [jobsiteContent, setJobsiteContent] = useState({title:"Create Jobsite", notification:"Jobsite saved successfully.", mode:'create'})

  const [searchContactText, setSearchContactText] = useState("");
  const [searchJobsiteText, setSearchJobsiteText] = useState('');
  const [searchCommLogText, setSearchCommLogText] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]);


  const MySwal = withReactContent(Swal);

  const navigate = useNavigate();
  
  const [BulkImportContent, setBulkImportContent] = useState({title:'', ImportURL:''});

  const [openBulkImportDlg, setOpenBulkImportDlg] = useState(false);
  const [EmpFiles, setEmpFiles] = useState([]);

  const [billingFrequencyID, setBillingFrequencyID] = useState(0);
  const [billingInfoChanged, setBillingInfoChanged] = useState(false);

  const handleCloseBulkImportDlg = () => {
    setOpenBulkImportDlg(false);    
  };

  const handleOpenBulkImportContactDlg = () => {
    setBulkImportContent({title:'Contacts', ImportURL: '/customers/add-bulk-contacts', customerID: customer.customerID})
    setOpenBulkImportDlg(true);   
  };
  const handleOpenBulkImportJobsiteDlg = () => {
    setBulkImportContent({title:'Jobsites', ImportURL : '/customers/add-bulk-jobsites', customerID: customer.customerID})
    setOpenBulkImportDlg(true);   
  };
  const location = useLocation();
  const currentPath = location.pathname;

  const fetchContactData = async () => {

    const token = localStorage.getItem('jwtToken');

    if (!token) {
      navigate('/login', { state: { message: 'Session expired, please login again.' } });
      return;
    }

    try {
      const response = await fetch(`${dev_baseUrl}/Contact/getContact/0/${customerID}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();      

      if (!data.isError) {
        const transformedData = data.result.map((item) => ({
          id: item.contactID,
          contact: `${item.firstName} ${item.lastName}`,
          email: item.email,
          initials: `${item.firstName[0]}${item.lastName[0]}`,
          jobTitle: item.jobTitle,
          primary: item.isPrimary,
          firstName: item.firstName,
          lastName: item.lastName,
          phoneNumbers:item.phoneNumbers, 
          customerID: item.customerID ,
          status:item.status
        })).sort((a, b) => (a.primary === b.primary ? 0 : a.primary ? -1 : 1));
        setDataAddContact(transformedData);
        setAllContacts(transformedData);
      } else {
        console.error('Failed to fetch contacts:', data.message);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };
  
  const fetchContacts = async() => {
    try {
      let con = await GetContacts(customerID);      
      setAllContacts(con);
    } catch (error) {
      console.error("Error fetching Customer data:", error);
    } finally {
      setLoading(false);
    }
  };

  // ADD: fetch ALL contacts across ALL customers for duplicate checks
  const fetchAllContactsSystemWide = async () => {
    const token = localStorage.getItem('jwtToken');
    if (!token) return;

    try {
      const res = await fetch(
        `${dev_baseUrl}/Contact/getContact/0/0`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!data.isError) {
        setGlobalContactsAll(Array.isArray(data.result) ? data.result : []);
      } else {
        console.error('Failed to fetch all contacts:', data.message);
      }
    } catch (err) {
      console.error('Error fetching all contacts:', err);
    }
  };

  useEffect(() => {
    if (customerID) {
      fetchContacts();
      fetchContactData();
      // GetBillingInfo(customerID)
    }
  }, [customerID]);

  useEffect(() => {
    if (customerID) {

    async function loadBilling() {
      const data = await GetBillingInfo(customerID)
      // console.log(data)
      if (data.isError || !data.result) return
      const r = data.result
      // 1) top-level frequency
      //    API uses "Recurring", "Monthly", etc → convert to your keys
      setBillingFrequencyID(r.billingFrequencyID);

      setBillingFreq(r.frequencyType?.toLowerCase())

      if (r.frequencyType?.toLowerCase() === 'recurring') {
        setRecurringType(r.billingFrequency?.toLowerCase())

        // 2) occurrences & start date
        // setOccurrences(r.numberOfOccurrences || 1)
        setCycleStartDate(r.cycleStartDate
          ? dayjs(r.cycleStartDate)
          : null
        )

        // 3) intervalDays (interval type)
        setIntervalDaysInput(r.intervalDays || 0)

        // 4) days[] → formatsBilling codes
        if (Array.isArray(r.days)) {
          const dayMap = {
            monday:   'mon',
            tuesday:  'tue',
            wednesday:'wed',
            thursday: 'thurs',
            friday:   'fri'
          }
          setFormatsBilling(
            r.days.map(d => dayMap[d.dayName?.toLowerCase()]).filter(Boolean)
          )
        }

        // 5) monthly week-of-month vs billing day
        if (r.billingFrequency?.toLowerCase() === 'monthly') {
          if (r.weekOfMonth) {
            setMonthlyOptions('weekOfMonth')
            setWeekOfMonth(r.weekOfMonth?.toLowerCase())
          } else {
            setMonthlyOptions('billingDay')
          }
        }

        // 6) quarterly → quarter & monthOfQuarter
        if (
          r.billingFrequency?.toLowerCase() === 'quarterly' &&
          Array.isArray(r.months) &&
          r.months.length
        ) {
          const m = r.months[0].monthName
          // get 0–11 index
          const idx = new Date(`${m} 1, 2000`).getMonth()
          const q  = Math.floor(idx/3) + 1
          setQuarter(`Q${q}`)
          setMonthOfQuarter((idx % 3) + 1)
        }
      }
    }

    loadBilling()

  }
  }, [customerID])

  useEffect(() => {
    const isEqual = _.isEqual(OldCustomer, customer);
    setIsChanged(!isEqual); 
    setHasUnsavedChanges(!isEqual)
  }, [OldCustomer, customer]);

  function formatDateSubtractDays(date, days) {
    // Create a copy of the date object to avoid mutating the original one
    const resultDate = new Date(date);
    
    // Subtract the specified number of days
    resultDate.setDate(resultDate.getDate() - days);
    
    // Extract year, month, and day from the resultDate
    const year = resultDate.getFullYear();
    const month = (resultDate.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-indexed
    const day = resultDate.getDate().toString().padStart(2, '0');
    
    // Return the date in yyyy-mm-dd format
    return `${year}-${month}-${day}`;
  }

  const isValidDate = (date) => {
    const parsedDate = new Date(date);
    return !isNaN(parsedDate.getTime()); // Returns true if valid, false otherwise
  };

  const validateDates = (startDate, endDate) => {
    // console.log("ValidateDates = "+ startDate, endDate)

    if (startDate === undefined || endDate === undefined || startDate === null || endDate === null) {
      // If either of the dates is undefined or null, allow the exception and return early
      return true; // Or continue without showing error
    }

    // Condition 1: Both Start and End Dates must be selected
    if (!isValidDate(startDate) || !isValidDate(endDate)) {
      toast.error("Both Start and End Dates must be selected.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
         theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });
      return false; // Return false to prevent further actions
    }
  
    // Convert startDate and endDate to Date objects if they are strings
    const start = new Date(startDate);
    const end = new Date(endDate);
  
    // Condition 2: Start Date cannot be greater than the End Date
    if (start > end) {
      toast.error("Start Date cannot be greater than End Date.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });
      return false;
    }
  
    // // Condition 3: The duration between Start Date and End Date cannot exceed 90 days
    // const duration = (end - start) / (1000 * 3600 * 24); // Difference in days
    // if (duration > 90) {
    //   toast.error("The duration between Start Date and End Date cannot exceed 90 days.", {
    //     position: "top-right",
    //     autoClose: 5000,
    //     hideProgressBar: true,
    //     closeOnClick: false,
    //     pauseOnHover: true,
    //     draggable: true,
    //     progress: undefined,
    //     theme: "light",
    //   });
    //   return false;
    // }
  
    return true; // If all conditions are met
  };

  const fetchLogs = async (startDate, endDate, pageNo, recordsPerPage) => {
    if(!hasCommunicationGetPermission) {
      console.log("Permission check failed - hasCommunicationGetPermission:", hasCommunicationGetPermission);
      return;
    }

    try {
      setLoading(true);      
      const accessToken = getToken();
      // const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        toast.error("Access token is missing. Please log in to proceed.", {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
        setLoading(false);
        return;
      }

      // console.log("Returned Dates ="+ startDate, endDate);

      if (validateDates(startDate, endDate)) {
        // Proceed with your logic if the dates are valid
        // console.log("Dates are valid, proceeding with further actions...");
      } else {
        // Handle the case when dates are invalid
        // console.log("Dates are invalid, cannot proceed.");
        return;
      }

      const startDate_New = startDate ? startDate : formatDateSubtractDays(new Date(), 90);
      const endDate_New = endDate ? endDate : formatDateSubtractDays(new Date(), 0);
      
      console.log("Fetching logs with dates:", { startDate_New, endDate_New, pageNo, recordsPerPage, customerID })


      const response = await fetch(
        `${dev_baseUrl}/CommunicationLog/getCommunicationLog?communicationLogID=0&customerID=${customerID}&entityID=0&entityType=&startDate=${startDate_New}&endDate=${endDate_New}&pageNo=${pageNo}&recordsPerPage=${recordsPerPage}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const resData = await response.json();
      // console.log(resData)

      if (!response.ok || resData.isError) {
        toast.error(resData?.message || "Failed to retrieve logs.", {
          position: "top-right",
          autoClose: 5000,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
        setLoading(false);
        return;
      }
      
      // Map the API result to grid data format
      const mappedLogs = resData.result.map((log) => ({        
        id: log.communicationLogID,
        communicationType: log.type,
        // date: new Date(log.communicationLogTime).toLocaleString(),
        date: log.communicationLogTime,
        description: log.description,
        record: log.entityType,
        recordid: log.entityID,
        followUp: new Date(log.followUpReminderTime).toLocaleString(),
        createdBy: log.createdBy || "Unknown",
        contactID: log.contactID,
        // totalRecords: log.totalRecords,
      }));
      console.log("Communication logs fetched successfully:", { 
        totalRecords: resData?.totalRecords, 
        mappedLogsCount: mappedLogs.length,
        mappedLogs 
      });
      setTotalRecords(resData?.totalRecords);
      setLogs(mappedLogs);
      // toast.success("Communication logs retrieved successfully.", {
      //   position: "top-right",
      //   autoClose: 5000,
      //   theme: "light",
      // });
    } catch (error) {
      console.error("Error fetching logs:", error);
      toast.error("An error occurred while fetching logs.", {
        position: "top-right",
        autoClose: 5000,
         theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });
    } finally {
      setLoading(false);
    }
  };


  const fetchLogsWithDateRange = (startDate, endDate) => {
    // console.log("Fetching logs with date range:", startDate, endDate);
    // Pass these dates to your fetchLogs function or directly call the API with these dates
    fetchLogs(startDate, endDate, 1, 10000);
  };

  useEffect(() => {
    if (customerID && hasCommunicationGetPermission) {
      console.log("Fetching logs on mount - Default dates:", {
        startDate: formatDateSubtractDays(new Date(), 90),
        endDate: formatDateSubtractDays(new Date(), 0),
        customerID
      });
      fetchLogs(null, null, 1, 10000);
    }
  }, [customerID, hasCommunicationGetPermission]);

  const filteredLogs = useMemo(() => {
    let temp = logs;
    
    // 1) by record type
    if (selectedTypes.length) {
      temp = temp.filter(r => selectedTypes.includes(r.record));      
    }
  
    // 2) by text
    const txt = searchCommLogText.trim().toLowerCase();
    if (txt) {
      temp = temp.filter(r =>
        Object.values(r).some(val =>
          String(val).toLowerCase().includes(txt)
        )
      );
    }
  
    return temp;
  }, [logs, selectedTypes, searchCommLogText]);

  // Pagination handler removed - now fetching all records at once
  // const handlePaginationModelComLog = (model) => {
  //   const newPageNo = model.page + 1; // Convert zero-based index to one-based
  //   const newRecordsPerPage = model.pageSize;
  
  //   setPaginationModel({
  //     pageNo: newPageNo,
  //     recordsPerPage: newRecordsPerPage,
  //   });
  
  //   fetchLogs(null, null, newPageNo, newRecordsPerPage);
  // };

  const columnsComLog = [
    {
      flex: 0.1,
      field: "communicationType",
      minWidth: 120,
      headerName: "Type",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Icon icon={
            row.communicationType === "Email" ? "tabler:mail" :
            row.communicationType === "Call" ? "tabler:phone-call" : 
            row.communicationType === "Text Message" ? "tabler:message-circle" :
            row.communicationType === "Mail/Physical Mail" ? "tabler:mailbox" : ""
            } />
          <Typography noWrap sx={{ ml: 2 }}>
          {row.communicationType === "Mail/Physical Mail"
            ? "Letter"
            : row.communicationType === "Text Message"
            ? "SMS"
            : row.communicationType}
          </Typography>
        </Box>
      ),
    },
    {
      flex: 0.25,
      sortable: true,
      field: "description",
      minWidth: 280,
      headerName: "Description",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography noWrap>{row.description}</Typography>
        </Box>
      ),
    },
    // {
    //   flex: 0.1,
    //   field: "date",
    //   minWidth: 210,
    //   headerName: "Log Date",
    //   renderCell: ({ row }) => (
    //     <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
    //       <Typography noWrap>{row.date}</Typography>
    //     </Box>
    //   ),
    // },
    {
      flex: 0.1,
      field: "date",
      minWidth: 220,
      headerName: "Log Date",
      renderCell: ({ row }) => {
        const formattedDate = format(new Date(row.date), "MM/dd/yyyy, hh:mm:ss a");
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap>{formattedDate}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      sortable: true,
      field: "record",
      minWidth: 140,
      headerName: "Record Type",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <CustomChip label={row.record} skin={theme.palette.mode === 'dark' ? 'dark' : "light"} color="primary" />
        </Box>
      ),
    },
    // {
    //   flex: 0.1,
    //   sortable: true,
    //   field: "recordID",
    //   minWidth: 140,
    //   headerName: "Record ID",
    //   renderCell: ({ row }) => (
    //     <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
    //       <Typography noWrap> {
    //         row.record === "Request" ? `REQ-${row.recordid.toString().padStart(3, "0")}` 
    //           : row.record === "Assessment" ? `AST-${row.recordid.toString().padStart(3, "0")}` 
    //           : row.record === "Estimate" ? `EST-${row.recordid.toString().padStart(3, "0")}`
    //           : row.record === "WorkOrder" ? `WO-${row.recordid.toString().padStart(3, "0")}` 
    //           : row.record === "Invoice" ? `INV-${row.recordid.toString().padStart(3, "0")}`
    //           : row.record === "Customer" ? `CUS-${row.recordid.toString().padStart(3, "0")}`
    //         : row.recordid          
    //         }
    //       </Typography>
    //     </Box>
    //   ),
    // },
        {
      flex: 0.1,
      sortable: true,
      field: "recordID",
      minWidth: 140,
      headerName: "Record ID",
      renderCell: ({ row }) => {
        // Map record type to prefix and route
        const prefixMap = {
          Request: "REQ",
          Assessment: "AST",
          Estimate: "EST",
          WorkOrder: "WO",
          Invoice: "INV",
          Customer: "CUS",
        };
        const routeMap = {
          Customer: `/customers/detail/${row.recordid}`,
          Request: `/requests/detail/${row.recordid}`,
          Estimate: `/estimates/detail/${row.recordid}`,
          Assessment: `/assessments/detail/${row.recordid}`,
          WorkOrder: `/work-orders/detail/${row.recordid}`,
          Invoice: `/invoices/detail/${row.recordid}`,
        };
        const prefix = prefixMap[row.record] || "";
        const displayId = prefix
          ? `${prefix}-${row?.recordid?.toString().padStart(3, "0")}`
          : row.recordid;
        const path = routeMap[row.record];

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography
              noWrap
              sx={{ cursor: path ? "pointer" : "default", textDecoration: path ? "underline" : "none" }}
              onClick={() => path && navigate(path)}
            >
              {displayId}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      sortable: true,
      field: "followUp",
      minWidth: 220,
      headerName: "Follow-up",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography noWrap>{row.followUp}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      sortable: true,
      field: "createdBy",
      minWidth: 50,
      headerName: "Created By",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography noWrap>{row.createdBy}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      sortable: false,
      field: "actions",
      minWidth: 120,
      headerName: "Actions",
      renderCell: ({ row }) => (
        <Box>
          <IconButton
            disabled={!hasCommunicationSavePermission}
            {...(hasCommunicationSavePermission && {
              onClick: () => handleOpenUpdateComLogDlg(row)
            })}
          > 
            <Icon icon="tabler:pencil" />
          </IconButton>
          <IconButton 
            color="error"
            disabled={!hasCommunicationDeletePermission}
            {...(hasCommunicationDeletePermission && {
              onClick: () => handleDeleteLog(row.id, customerID)
            })}
          >
            <Icon icon="tabler:trash" />
          </IconButton>
        </Box>
      ),
    },
  ];

  // const handleJobsiteDelete = async (jobsiteID,jobsiteName) => {
  //   try {
  //     const result = await MySwal.fire({
  //       title: 'Delete Jobsite',
  //       html: `Are you sure you want to delete ${jobsiteName} jobsite ?`,
  //       icon: 'warning',
  //       showCancelButton: true,
  //       confirmButtonColor: theme.palette.main.main,
  //       cancelButtonColor: theme.palette.error.main,
  //       confirmButtonText: 'Yes, Proceed!',
  //       cancelButtonText: 'Cancel',
  //     });

  //     if (result.isConfirmed) {
  //       const deleteResult = await DeleteJobsite(jobsiteID, customerID, navigate);
        
  //       if (deleteResult && !deleteResult.isError) {
  //         fetchJobsites();
  //         toast.success(`"${jobsiteName}" Jobsite deleted successfully.`, {
  //           position: 'top-right',
  //           autoClose: 5000,
  //           hideProgressBar: false,
  //           closeOnClick: false,
  //           pauseOnHover: true,
  //           draggable: true,
  //           progress: undefined,
  //           theme: 'light',
  //         });
  //       } else {
  //         toast.error(deleteResult.message || 'Failed to delete the jobsite.', {
  //           position: 'top-right',
  //           autoClose: 5000,
  //           hideProgressBar: false,
  //           closeOnClick: false,
  //           pauseOnHover: true,
  //           draggable: true,
  //           progress: undefined,
  //           theme: 'light',
  //         });
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error deleting jobsite:', error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleDeleteLog = async (communicationLogID, customerID) => {
    if (!communicationLogID || !customerID) {
      toast.error("Invalid log details for deletion.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });
      return;
    }
  
    const accessToken = getToken();
    const url = `${dev_baseUrl}/CommunicationLog/deleteCommunicationLog/${communicationLogID}/${customerID}/0`;
  
    try {
      const result = await MySwal.fire({
        title: 'Delete Communication Log',
        html: `Are you sure you want to delete this log ?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: theme.palette.main.main,
        cancelButtonColor: theme.palette.error.main,
        confirmButtonText: 'Yes, Proceed!',
        cancelButtonText: 'Cancel',
      });
      if (result.isConfirmed) {
        // const deleteResult = await DeleteJobsite(jobsiteID, customerID, navigate);
        const response = await fetch(url, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (response && !response.isError) {
          // fetchLogs(); // Refresh logs after deletion
          fetchLogs(null, null, 1, 10000)
          toast.success("Communication log deleted successfully!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
             theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
        } else {
          const error = await response.json();
          toast.error(error.message || "Failed to delete log. Please try again.", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
             theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
        }
      }
      // const response = await fetch(url, {
      //   method: "DELETE",
      //   headers: {
      //     Authorization: `Bearer ${accessToken}`,
      //   },
      // });      
  
      // if (response.ok) {
      //   toast.success("Communication log deleted successfully!", {
      //     position: "top-right",
      //     autoClose: 5000,
      //     hideProgressBar: true,
      //     closeOnClick: false,
      //     pauseOnHover: true,
      //     draggable: true,
      //     progress: undefined,
      //     theme: "light",
      //   });
  
      //   fetchLogs(); // Refresh logs after deletion
      // } else {
      //   const error = await response.json();
      //   toast.error(error.message || "Failed to delete log. Please try again.", {
      //     position: "top-right",
      //     autoClose: 5000,
      //     hideProgressBar: true,
      //     closeOnClick: false,
      //     pauseOnHover: true,
      //     draggable: true,
      //     progress: undefined,
      //     theme: "light",
      //   });
      // }
    } catch (error) {
      console.error("Error deleting log:", error);
      toast.error("An error occurred while deleting the log. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });
    }
  };
  

  const handleJobsiteChange = (field, value) => {
    if(field === 'billingAddress'){
        if(!value){
          setJobsite((prevState) => ({ ...prevState,
            [field]: value ,
            ['street1']:'',
            ['street2']:'',
            ['floorLevel']:'',
            ['unitNumber']:'',
            ['city']:'',           
            ['state']:'',
            ['zipCode']:'',
            ['county']:'',
          })); 
        }else{
          setJobsite((prevState) => ({ ...prevState,
            [field]: value ,
            ['street1']:customer?.companyAddress_Street1 || '',
            ['street2']:customer?.companyAddress_Street2 || '',
            ['floorLevel']:customer?.companyAddress_FloorLevel || '',
            ['unitNumber']:customer?.companyAddress_UnitNumber || '',
            ['city']:customer?.companyAddress_City || '',           
            ['state']:customer?.companyAddress_State || '',
            ['zipCode']:customer?.companyAddress_ZipCode || '',
            ['county']:customer?.companyAddress_County || '',
          })); 
        }      
      }
      else if(field.includes("zipCode")){
        if(isNumberOnly(value)){
          setJobsite((prevState) => ({ ...prevState, [field]: value }));
        }
        else{
        setJobsite((prevState) => ({ ...prevState, [field]: value }));
      }        
    }else{
      setJobsite((prevState) => ({ ...prevState, [field]: value }));    
    }    
  };
  // const isNumberOnly = (input) => {
  //   const numberRegex = /^(?:\d+|\d*\.\d+)$/; // Matches one or more digits only
  //   return numberRegex.test(input);
  // }  

  const clearAreaMeasurement = () =>{
    setAreaMeasurement({
      measurementName:null, 
      unitOfMeasure:null,
      length:0,
      width:0,
      height:0,
      total:0,
    });   
  }


  const handleMarkAsPrimary = async (contactID) => {
    const token = localStorage.getItem('jwtToken');
  
    if (!token) {
      navigate('/login', { state: { message: 'Session expired, please login again.' } });
      return;
    }
 
    // Set only the selected contact as primary, others as false
    const updatedContacts = dataAddContact.map(contact => ({
      ...contact,
      isPrimary: contact.id === contactID ? true : false // Only the selected contact will be true
    }));
  
    // Prepare contactData for API
    const contactData = updatedContacts.map(contact => ({
      contactID: contact.id,
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      jobTitle: contact.jobTitle,
      customerID: parseInt(customerID),
      isPrimary: contact.isPrimary,
    }));
    setIsMarkingPrimary(true);
    try {
      const response = await fetch(`${dev_baseUrl}/Contact/saveContact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(contactData),
      });
            
      const resData = await response.json();      
  
      if (response.ok && !resData?.isError) {
        toast.success(resData?.message || 'Successfully Marked as Primary', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
  
        // Update state with the modified contacts
        setDataAddContact(updatedContacts);
        fetchContactData();
      } else {
        toast.error(resData?.message || 'Failed to mark as primary', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
         theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    } catch (error) {
      console.error("Error updating primary contact:", error);
    } finally {
      setIsMarkingPrimary(false)
    }
  };
  

  // ** Handlers

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
  
    const handleBackButton = (event) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
           handleOpenUnsavedChangesDlg('navigate');

        

        // MySwal.fire({
        //   title: "Are you sure?",
        //   html: "This page has unsaved changes",
          
        //   showCancelButton: true,
        //   confirmButtonColor: "#23A26D",
        //   cancelButtonColor: "#d33",
        //   confirmButtonText: "Discard Changes",
        //   cancelButtonText: "Cancel",
        //   allowEscapeKey: true,
        // }).then((result) => {
        //   if (result.isConfirmed) {
        //     navigate('/customers');
        //     setHasUnsavedChanges(false);
        //   }
        // });
      }else{
        navigate('/customers')
      }
    };
  
    window.history.pushState(null, '', window.location.href);
  
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handleBackButton);
  
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [hasUnsavedChanges]);

  const handleOpenInviteDialog = () => setOpenInviteDialog(true)
  const handleCloseInviteDialog = () => setOpenInviteDialog(false)

  const handleOpenCustomerDeactivateDlg = () => setOpenDeactiveCustomerDlg(true)
  const handleCloseCustomerDeactivateDlg = () => setOpenDeactiveCustomerDlg(false)

  const handleOpenNewContactDlg = () => setOpenNewContactDlg(true);
  const handleCloseNewContactDlg = () => setOpenNewContactDlg(false);

  const handleEditContactClick = (id) => {
    setContactID(id);
    handleUpdateContactDlg();
  }

  const clearPropertyArea = () => {
    setPropertyArea(InitialpropertyArea);
  }

  const handleClearContactSearch = () => {
    handleContactSearchChange("");
  };

  const handleContactSearchChange = (searchKeyWord) => {
    setSearchContactText(searchKeyWord);
  };

  const handleClearJobsiteSearch = () => {
    handleJobsiteSearchChange("");
  };

  const handleJobsiteSearchChange = (searchKeyWord) => {
    setSearchJobsiteText(searchKeyWord);
  };

  const filteredContactRows = hasContactGetPermission && Array.isArray(allContacts)
  ? allContacts.filter(row =>
      Object.values(row).some(value =>
        value && value.toString().toLowerCase().includes(searchContactText.toLowerCase())
      )
    )
  : [];


  const handleJobsiteEdit = (jobsiteID) => {
    let getJobSite = allJobsites.filter(x => x.jobsiteID === jobsiteID)[0];
    setJobsite(getJobSite);
    setOldJobsite(getJobSite);   
    setActiveStepJobsite(0); 
    fetchContacts();
    fetchPropertyArea(getJobSite?.jobsiteID);
    setOpenNewJobsiteDlg(true);
    setJobsiteContent({title:"Edit Jobsite", notification: `Jobsite updated successfully.`, mode:'edit'});
  }

  const handleJobsiteDelete = async (jobsiteID,jobsiteName) => {
    try {
      const result = await MySwal.fire({
        title: '<strong>Are you sure?</strong>',
        html: `<span>Are you sure you want to delete ${jobsiteName} jobsite?</span>`,
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'No, keep it',
        width: '390px', // Explicit width control
        padding: '1rem',
        focusConfirm: false,
        showCloseButton: true,
        closeButtonHtml: '&times;', // This is the × symbol
        customClass: {
          popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
          cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
          closeButton: 'custom-swal-close'
        }
      });

      if (result.isConfirmed) {
        const deleteResult = await DeleteJobsite(jobsiteID, customerID, navigate);
        
        if (deleteResult && !deleteResult.isError) {
          fetchJobsites();
          toast.success(`"${jobsiteName}" Jobsite deleted successfully.`, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
             theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
        } else {
          toast.error(deleteResult.message || 'Failed to delete the jobsite.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
        }
      }
    } catch (error) {
      console.error('Error deleting jobsite:', error);
    } finally {
      setLoading(false);
    }
  };
  

  const fetchAlerts = async(jobsiteID) =>{
    try {
      setLoading(true);
      let alrts = await GetAlerts(jobsiteID,0,navigate);      
      setAlertList(alrts);           
    } catch (error) {
      console.error("Error fetching Alerts data:", error);
    } finally {
      setLoading(false);
    }
  }

  const handleDeleteContact = async (contactID) => {
    const token = localStorage.getItem('jwtToken');
    
    if (!token) {
      navigate('/login', { state: { message: 'Session expired, please login again.' } });
      return;
    }
    setIsDeleting(true);

    try {
      const response = await fetch(`${dev_baseUrl}/Contact/deleteContact/${contactID}/${customerID}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`, // Use the token stored in local storage
          'Content-Type': 'application/json',
        },
      });      

      const data = await response.json();
                    
      if (response.ok && !data.isError) {
        toast.success(data.message || 'Contact deleted successfully.', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
        fetchContactData(); // Refetch attachments after successful delete
      } else {
        toast.error(data.message || 'Failed to delete the contact.', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    } catch (error) {
      console.error('An error occurred while deleting the contact:', error);
      // Handle network or unexpected errors
    } finally {
      setIsDeleting(false);
    }
  };

  // ===============================================================
  const columnsAddContact = [
    {
      flex: 0.1,
      hide: true,
      field: "id",
      minWidth: 80,
      headerName: "ID",
      renderCell: ({ row }) => {
        const { id } = row;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{id}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "contact",
      minWidth: 120,
      headerName: "Contact",
      renderCell: ({ row }) => {
        const { contact, email, initials } = row;
  
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: "100%",
              gap: 2
            }}
          >
            <CustomAvatar
              sx={{ height: 30, width: 30, fontSize: "12px" }}
              color='success'
            >
              {initials}
            </CustomAvatar>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Typography noWrap>{contact}</Typography>
              <Typography noWrap variant="caption">
                {email}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "jobTitle",
      minWidth: 120,
      headerName: "Job Title",
      renderCell: ({ row }) => {
        const { jobTitle } = row;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <CustomChip
              rounded
              size="small"
              skin="light"
              sx={{ lineHeight: 1 }}
              color="secondary"
              label={jobTitle}
            />
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      sortable: false,
      field: "primary",
      minWidth: 120,
      headerName: "Contact Type",
      renderCell: ({ row }) => {
        const { primary, id } = row;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            {primary === true && (
              <CustomChip
                rounded
                size="small"
                skin="light"
                sx={{ lineHeight: 1 }}
                color="success"
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    <Icon icon='tabler:check' />
                    Primary
                  </Box>
                }
              />
            )}
            {primary === false && (
              <CustomChip
                rounded
                size="small"
                skin="dark"
                sx={{ lineHeight: 1 }}
                color="secondary"
                label="Mark as Primary"
                onClick={ () => handleMarkAsPrimary(id)}
                disabled={isMarkingPrimary}
              />
            )}
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      sortable: false,
      field: "actions",
      minWidth: 100,
      headerName: "Actions",
      renderCell: ({row}) => {
        const { primary, id } = row
  
        return (
          <Box>
            <IconButton
              disabled={!hasContactSavePermission}
              {...(hasContactSavePermission && {
                onClick: () => handleEditContactClick(id)
              })}
            >
              <Icon icon="tabler:pencil" />
            </IconButton>
            { !primary && (
              <IconButton
                {...(hasContactDeletePermission && {
                  onClick: () => handleDeleteContact(id)
                })} 
                color='error' 
                disabled={!hasContactDeletePermission || isDeleting}
              >
                <Icon icon="tabler:trash" />
              </IconButton>
            ) }
          </Box>
        )
      },
    },
  ];
  // ===============================================================

  const handleUpdateContactDlg = () => setUpdateContactDlg(true);
  const handleCloseUpdateDlg = () => setUpdateContactDlg(false);

  const handleQuickViewClose = () => {
    setQuickViewOpen(false);
    setPropertyAreaList([]); 
    //setAllContacts([]);
    setAlertList([]);
    setJobsite(initialJobsiteState);
  };

  const handleQuickViewOpen = (jobsiteID) => {
    let getJobSite = allJobsites.filter(x => x.jobsiteID === jobsiteID)[0];
    fetchAlerts(jobsiteID);
    fetchContacts();
    fetchPropertyArea(jobsiteID);
    setJobsite(getJobSite);    
    setQuickViewOpen(true)
  }  

  const handleOpenNewJobsiteDlg = () => {       
    setJobsite(initialJobsiteState);   
    fetchContacts();
    setActiveStepJobsite(0);
    setOpenNewJobsiteDlg(true); 
    setFiles([]);
    setJobsiteContent({title:"Create Jobsite", notification:"Jobsite saved successfully.", mode: 'create'});   
  }

  const handleCloseNewJobsiteDlg = (e) => {
    // Handler to prevent dialog from closing on Escape when Swal is active
    const escHandler = (event) => {
      if (event.key === "Escape") {
        if (MySwal.isVisible()) {
          MySwal.close();      
        } else {          
          event.stopPropagation();
          event.preventDefault();
        }
      }
    };
  
    const isEqual = _.isEqual(initialJobsiteState, jobsite);
  
    // If in 'create' mode and there are unsaved changes
    if (jobsiteContent.mode === "create" && !isEqual) {
      // Add a global Escape key listener
      document.addEventListener("keydown", escHandler);
  
      MySwal.fire({
       title: '<strong>Are you sure?</strong>',
    html: '<span>This page has some unsaved changes</span>',
    showCancelButton: true,
    confirmButtonText: 'Yes, discard changes!',
    cancelButtonText: 'No, stay here',
     width: '420px', // Explicit width control
  padding: '1rem',
    focusConfirm: false,
     showCloseButton: true,
     closeButtonHtml: '&times;', // This is the × symbol
    customClass: {
      popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
         cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
          closeButton: 'custom-swal-close'
    }
  }).then((result) => {
        if (result.isConfirmed) {
          let result = DeleteJobsite(jobsite.jobsiteID, customerID, navigate);
          fetchJobsites();
          setOpenNewJobsiteDlg(false); // Close the dialog after confirming
        }
      });
    }
    // If in 'edit' mode and there are unsaved changes
    else if (jobsiteContent.mode === "edit" && !_.isEqual(Oldjobsite, jobsite)) {
      // Add a global Escape key listener
      document.addEventListener("keydown", escHandler);
  
      MySwal.fire({
       title: '<strong>Are you sure?</strong>',
    html: '<span>This page has some unsaved changes</span>',
    showCancelButton: true,
    confirmButtonText: 'Yes, discard changes!',
    cancelButtonText: 'No, stay here',
     width: '420px', // Explicit width control
    padding: '1rem',
    focusConfirm: false,
     showCloseButton: true,
     closeButtonHtml: '&times;', // This is the × symbol
    customClass: {
       popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
         cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
          closeButton: 'custom-swal-close'
    }
  }).then((result) => {
        if (result.isConfirmed) {
          setOpenNewJobsiteDlg(false); // Close the dialog after confirming
        }
      });
    }
    // If no unsaved changes, close the dialog directly
    else {
      setOpenNewJobsiteDlg(false);
    }
  };
  

  // const handleOpenNewComLogDlg = () => setOpenNewComLogDlg(true)
  // const handleCloseNewComLogDlg = () => setOpenNewComLogDlg(false)

  const handleOpenNewComLogDlg = () => {
    setSelectedLog(null);
    setComLogDialogDate(new Date()); // Set date only when opening dialog
    setOpenNewComLogDlg(true);
  };

  const handleOpenUpdateComLogDlg = (log) => {
    setSelectedLog(log); // Indicate it's an "update" action and pass the log data
    setOpenNewComLogDlg(true);
  };

  const handleCloseNewComLogDlg = () => {
    setSelectedLog(null);
    setOpenNewComLogDlg(false);
    // setSelectedLog(null); // Reset to prepare for future "create" actions
  };

  const mandatoryFields = [
    { key: "firstName", displayName: "First Name" },
    { key: "lastName", displayName: "Last Name" },
    { key: "companyName", displayName: "Company Name" },
    { key: "email", displayName: "Email" },
    { key: "phoneNumbers", displayName: "Phone Number" },
    { key: "companyAddress_Street1", displayName: "Street 1" },
    { key: "companyAddress_City", displayName: "City" },
    { key: "companyAddress_State", displayName: "State" },
    { key: "companyAddress_ZipCode", displayName: "Zip Code" },
  ];
  
  const validateIndiviual = (field, value) => {
    const fieldInfo = mandatoryFields.find((f) => f.key === field);
    let displayName = fieldInfo?.displayName || field;

    if (value) {
      setErrors((prevState) => ({ ...prevState, [field]: '' }));
    } else {
      setErrors((prevState) => ({
        ...prevState,
        [field]: `${displayName} is required.`
      }));
    }
  }


  const isNumberOnly = (input) => {
    const numberRegex = /^\d*$/; 
    return numberRegex.test(input);
  }
  
const handleChange = async (field, value) => {
  const nameRegex = /^[A-Za-z]+$/;

  if (field === 'firstName' || field === 'lastName') {
    if (value && !nameRegex.test(value)) {
      return;
    }
  }

  if (field === "companyAddress_ZipCode") {
    const zipCodeRegex = /^\d{0,5}$/;
    if (zipCodeRegex.test(value)) {
      setCustomer((prevState) => ({ ...prevState, [field]: value }));
      
      if (value.length === 5) {
        const timezone = await getTimeZone(value);
        const { county } = await getCounties(value);

        setCustomer((prevState) => ({
          ...prevState,
          timeZone: timezone,
          companyAddress_County: county || '',
        }));
      }
    }
  }
  
  else if (field === "billingAddress_ZipCode") {
    const zipCodeRegex = /^\d{0,5}$/;
    if (zipCodeRegex.test(value)) {
      setCustomer((prevState) => ({ ...prevState, [field]: value }));
      
      if (value.length === 5) {
        // Get county for billing address zip code
        const { county } = await getCounties(value);
        
        setCustomer((prevState) => ({
          ...prevState,
          billingAddress_County: county || '',
        }));
      }
    }
  }
  else {
    setCustomer((prevState) => ({ ...prevState, [field]: value }));
  }

  if(mandatoryFields.filter((f) => f.key === field).length > 0){
    validateIndiviual(field, value)
  }

  if (field === 'email') {
    const dupe = isEmailTaken(value);
    setErrors(prev => ({
      ...prev,
      email: dupe
        ? 'This email is already taken.'
        : (prev.email === 'This email is already taken.' ? '' : prev.email)
    }));
  }
};
const handleStateChange = (value) => {
  if (!value) {
    handleChange("companyAddress_State", "");
    return;
  }

  const fullName = expandStateName(value);
  handleChange("companyAddress_State", fullName);
};
  const handleAddPhoneField = () => {
    const phoneRegex = /^\d{10}$/;
    setCustomer((prev) => {
      const allPhoneNumbersValid = prev.phoneNumbers?.every((phone, index) => {
        const IsValidPhone = phone?.phoneNo?.trim() !== '' && phone?.phoneNo?.trim() !== '1' && phoneRegex.test(phone.phoneNo);       
        if (!IsValidPhone) {          
          setPhoneError((prevErrors) => ({
            ...prevErrors,
            [index]: 'Please enter a valid phone number before adding another.',
          }));
        } else {
          setPhoneError((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[index]; 
            return newErrors;
          });
        }
  
        return IsValidPhone;
      });
  
  
      if (allPhoneNumbersValid) {
        return {
          ...prev,
          phoneNumbers: [
            ...prev.phoneNumbers,
            { phoneNo: '', isPrimary: false },
          ],
        };
      } else {              
        return prev;
      }
    });
  };
  const handlePhoneFieldChange = (index, value) => {
    const phoneRegex = /^\d{10}$/;
  
    const isDuplicateInForm = customer && Array.isArray(customer.phoneNumbers) &&
      customer.phoneNumbers.some((phoneNumber, i) =>
        phoneNumber &&
        phoneNumber.phoneNo &&
        i !== index &&
        phoneNumber.phoneNo === value &&
        value !== '' &&
        value.length >= 10
      );
  
    const phoneDigits = normalizePhone(value);
    const isFormatValid = phoneRegex.test(phoneDigits);
  
    // ADD: system-wide check (excludes current customer via our indices)
    const isTakenGlobally = isPhoneTaken(value);
  
    if (isDuplicateInForm) {
      setPhoneError(prev => ({ ...prev, [index]: 'Phone number already exists in this customer.' }));
      setIsValidPhone(prev => ({ ...prev, [index]: false }));
    } else if (!isFormatValid && (value !== '' && value !== '1')) {
      setPhoneError(prev => ({ ...prev, [index]: 'Invalid phone number.' }));
      setIsValidPhone(prev => ({ ...prev, [index]: false }));
    } else if (value === '1' || value === '') {
      setPhoneError(prev => ({ ...prev, [index]: 'Phone number is required.' }));
      setIsValidPhone(prev => ({ ...prev, [index]: false }));
    } else if (isTakenGlobally) { // ADD
      setPhoneError(prev => ({ ...prev, [index]: 'This phone number is already taken.' }));
      setIsValidPhone(prev => ({ ...prev, [index]: false }));
    } else {
      setPhoneError(prev => ({ ...prev, [index]: '' }));
      setIsValidPhone(prev => ({ ...prev, [index]: true }));
    }
  
    const updatedPhoneNumbers = customer.phoneNumbers.map((phoneNumber, i) => {
      if (i === index && index === 0 && customer.phoneNumbers.filter(x => x.isPrimary === true).length === 0) {
        return { ...phoneNumber, phoneNo: value, isPrimary: true };
      } else if (i === index) {
        return { ...phoneNumber, phoneNo: value };
      } else return phoneNumber;
    });
  
    setCustomer(prev => ({ ...prev, phoneNumbers: updatedPhoneNumbers }));
  };  

  // const handlePhoneFieldChange = (index, value) => {

  //   const phoneRegex = /^\d{10}$/;

  //   const isDuplicate = customer && Array.isArray(customer.phoneNumbers) && 
  //     customer.phoneNumbers.some((phoneNumber, i) => 
  //     phoneNumber && 
  //     phoneNumber.phoneNo && 
  //     i !== index && 
  //     phoneNumber.phoneNo === value && 
  //     value !== '' && 
  //     value.length >= 10
  //   );

  //   const isValidFormat = phoneRegex.test(value);

  //   if (isDuplicate) {      
  //     setPhoneError((prev) => ({        
  //       ...prev,
  //       [index]: 'Phone number already exists.',
  //     }));
  //     setIsValidPhone((prev)=>({
  //       ...prev,
  //       [index]: false
  //     }));
  //   }else if (!isValidFormat && (value !== '' && value !== '1')) {
  //       setPhoneError((prev) => ({
  //           ...prev,
  //           [index]: 'Invalid phone number.',
  //       }));
  //       setIsValidPhone((prev)=>({
  //         ...prev,
  //         [index]: false
  //       }));
  //   }else if(value === '1' || value === ''){
  //     setPhoneError((prev) => ({
  //       ...prev,
  //       [index]: 'Phone number is required.',
  //   }));
  //     setIsValidPhone((prev)=>({
  //       ...prev,
  //       [index]: false
  //     }));
  //   } else {
  //     setPhoneError((prev) => ({
  //       ...prev,
  //       [index]: '', // Clear the error
  //     }));
  //     setIsValidPhone((prev)=>({
  //       ...prev,
  //       [index]: true
  //     }))
  //   }

  //   const updatedPhoneNumbers = customer.phoneNumbers.map((phoneNumber, i) => {
  //     if (i === index && index === 0 && customer.phoneNumbers.filter(x => x.isPrimary === true).length === 0 ) {
  //       return { ...phoneNumber, phoneNo: value, isPrimary: true }; 
  //     }else if(i === index){
  //       return { ...phoneNumber, phoneNo: value };
  //     }else
  //     return phoneNumber;
  //   });
  //   setCustomer((prev) => ({
  //     ...prev,
  //     phoneNumbers: updatedPhoneNumbers,
  //   }));
  // };

const handleDeletePhoneField = (index) => {
  MySwal.fire({
    title: '<strong>Are you sure?</strong>',
    html: '<span>You won\'t be able to revert this!</span>',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'No, keep it',
    width: '390px',
    padding: '1rem',
    focusConfirm: false,
    showCloseButton: true,
    closeButtonHtml: '&times;',
    customClass: {
      popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
          cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
          closeButton: 'custom-swal-close'
    }
  }).then((result) => {
    if (result.isConfirmed) {
      const updatedPhoneNumbers = customer.phoneNumbers.filter((_, i) => i !== index);

      setCustomer((prev) => ({
        ...prev,
        phoneNumbers: updatedPhoneNumbers,
      }));

      setPhoneError((prev) => {
        const updatedErrors = { ...prev };
        delete updatedErrors[index];
        return updatedErrors;
      });
    }
  });
};


  const handleSetPrimary = (index) => {
    const isDuplicate = customer.phoneNumbers.some((phoneNumber, i) => 
      i !== index && phoneNumber.phoneNo === customer.phoneNumbers[index].phoneNo // Check for duplicates excluding the current
    );
    if (!isDuplicate) {
      const updatedPhoneNumbers = customer.phoneNumbers.map((phoneNumber, i) => {
        return { ...phoneNumber, isPrimary: i === index }; // Set current as primary and others as not primary
      });

      setCustomer((prev) => ({
        ...prev,
        phoneNumbers: updatedPhoneNumbers,
      }));
    }
  };

  const handleSendInvite = () => {
    setOpenInviteDialog(false)
    
    toast.success(`Invite Sent!`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
  }

  const fetchData = async (cusID) => {
    if(!hasGetPermission) return ;

    try {      
      setLoading(true);
      let cus = await GetCustomer(cusID, navigate);  
      if(cus[0].phoneNumbers?.length === 0 || cus[0].phoneNumbers === null){
        cus[0].phoneNumbers = phoneFields;
      }         
      setCustomer(cus[0]);
      SetOldCustomer(cus[0]);       
      setLoading(false);   
    } catch (error) {
      console.error("Error fetching Customer data:", error);
    }finally {
      setLoading(false); 
    }
  };

  const fetchPropertyArea = async (jobsiteID,PropertyAreaID = 0) => {    
    try {      
      setLoading(true);
      let PA = await GetPropertyArea(jobsiteID,PropertyAreaID,navigate);                 
      setPropertyAreaList(PA);   
    } catch (error) {
      console.error("Error fetching PropertyArea data:", error);
    }finally {
      setLoading(false); 
    }
  };

  
  const fetchJobsites = async () => {
    try {
      let job = await GetJobsite(customerID,0, navigate);
      //console.log(job);
      setAllJobsites(job);
    } catch (error) {
      console.error("Error fetching Jobsite data:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData(customerID);
    fetchJobsites();
    fetchAllContactsSystemWide();
    //getAttachmentThumbnail();
  }, [hasGetPermission]);

  // ADD: Build indices anytime inputs change
  useEffect(() => {
    const eSet = new Set();
    const pSet = new Set();

    // 1) From all customers (exclude current)
    (allCustomers || []).forEach(c => {
      if (String(c.customerID) === String(customerID)) return;
      if (c.email) eSet.add(normalizeEmail(c.email));

      // include customer-level phones if available
      if (Array.isArray(c.phoneNumbers)) {
        c.phoneNumbers.forEach(p => {
          if (p?.phoneNo) pSet.add(normalizePhone(p.phoneNo));
        });
      }
    });

    // 2) From all contacts in the system (exclude current customer's contacts)
    (globalContactsAll || []).forEach(cnt => {
      if (String(cnt.customerID) === String(customerID)) return;

      if (cnt.email) eSet.add(normalizeEmail(cnt.email));

      // could be array or single
      if (Array.isArray(cnt.phoneNumbers)) {
        cnt.phoneNumbers.forEach(p => {
          if (p?.phoneNo) pSet.add(normalizePhone(p.phoneNo));
        });
      } else if (cnt.phoneNo) {
        pSet.add(normalizePhone(cnt.phoneNo));
      }
    });

    setEmailIndex(eSet);
    setPhoneIndex(pSet);
  }, [allCustomers, globalContactsAll, customerID]);

  useEffect(() => {
    const fetchThumbnails = async () => {
      const newThumbnails = {};
  
      for (const date in groupedAttachments) {
        for (const attachment of groupedAttachments[date]) {
          const imgUrl = await getAttachmentThumbnail(customerID, attachment.fileName);
          newThumbnails[attachment.fileName] = imgUrl;
        }
      }
  
      setThumbnails(newThumbnails);
    };
  
    fetchThumbnails();
  }, [groupedAttachments]);


  const token = localStorage.getItem('jwtToken');

  const fetchAttachments = async () => {
    if (!customer.customerID) return;

    try {
      setIsAttachmentsLoading(true);
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${dev_baseUrl}/Media/getmedia/Attachment/Customer/${customer.customerID}/0`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (response.ok && !data.isError) {
        
        setAttachments(data.result);
      } else {
        console.error("Failed to fetch attachments:", data.message);
      }
    } catch (error) {
      console.error("Error fetching attachments:", error);
    } finally {
      setIsAttachmentsLoading(false);
    }
  };

  useEffect(() => {
    if (customer.customerID) {
      fetchAttachments();
    }
  }, [customer.customerID]);

  useEffect(() => {    
    if(customer?.phoneNumbers){
      PhoneResolve(); 
    }   
  }, [customer]);

  useEffect(() => {
    const grouped = attachments.reduce((acc, attachment) => {
      const parsedDate = new Date(attachment.createDate);

      // Check if the date is valid
      if (!isNaN(parsedDate.getTime())) {
        const dateKey = format(parsedDate, 'MMMM dd, yyyy'); // Group by date

        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(attachment);
      }
      return acc;
    }, {});

    setGroupedAttachments(grouped);
  }, [attachments]); // Re-run grouping when attachments change

  const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(ext)) return 'video';
    if (['txt', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'json', 'xml'].includes(ext)) return 'text';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) return 'image';
    return 'other';
  };

  const getAttachmentThumbnail = async (customerID, filename) => {
    const fileExtension = filename.split('.').pop()?.toLowerCase();

    const fileType = getFileType(filename)
    if(fileType === 'video'){
      return VideoPlaceholderIcon;
    }else if (fileType === 'text'){
      if(fileExtension === 'csv'){
        return CSV_PlaceholderIcon
      }else if(fileExtension === 'pdf'){
        return PDF_PlaceholderIcon
      }else if(fileExtension === 'doc' || fileExtension === 'docx'){
        return DOCX_PlaceholderIcon
      }else if(fileExtension === 'xls' || fileExtension === 'xlsx'){
        return XLSX_PlaceholderIcon
      }
    }else if(fileType === 'image'){
      try {
        const blob = await DownloadAttachments('Attachment','Customer', customerID, filename, navigate);
        const imgurl = URL.createObjectURL(blob);
  
        return imgurl
      }catch (error){
        console.error("Failed to fetch media thumbnail", error)
      }
    }else{
      return FilePlaceholder
    }
  };

  const downloadAttachment = async (entityID,fileName) => {        
          try {
            // Call the service function to fetch the file data
            setLoading(true) ;
            const response = await DownloadAttachments('Attachment', 'Customer', entityID, fileName, navigate);
        
            if (response && response.size > 0) {
              // Check if the response contains valid data
              //const blob = new Blob([response], { type: response.type || 'application/octet-stream' });
        
              // Use file-saver to trigger a download in the browser
              saveAs(response, fileName);
              
                toast.success(`File downloaded successfully: ${fileName}`, {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: false,
                  pauseOnHover: true,
                  draggable: true,
                  progress: undefined,
                  theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
                  });                               
            } else {       
                toast.error(`No data received for the requested file.`, {
                  position: "top-right",
                  autoClose: 5000,
                  hideProgressBar: false,
                  closeOnClick: false,
                  pauseOnHover: true,
                  draggable: true,
                  progress: undefined,
                   theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
                  });               
            }
          } catch (error) {
            console.error('Error downloading the file:', error);
          }finally{
            setLoading(false);
          }
        };

  // const downloadAttachment = async (customerID, fileName) => {
  //   try {
  //     // Construct the API URL
  //     const url = `/api/Media/download/Attachment/Customer/${customerID}/${fileName}`;
  
  //     // Make the API call to download the file
  //     const response = await fetch(url, {
  //       method: 'GET',  // Use GET to download
  //       headers: {
  //         'Authorization': `Bearer ${localStorage.getItem('authToken')}`, // Include auth token
  //       },
  //     });
  
  //     if (!response.ok) {
  //       // throw new Error('Failed to download the file');
  //       toast.error('Failed to download the file', {
  //         position: "top-right",
  //         autoClose: 5000,
  //         hideProgressBar: true,
  //         closeOnClick: false,
  //         pauseOnHover: true,
  //         draggable: true,
  //         progress: undefined,
  //         theme: "light"
  //       });
  //       return;
  //     }
  
  //     // Get the file as a blob
  //     const blob = await response.blob();
  
  //     // Create a link element to trigger the download
  //     const link = document.createElement('a');
  //     link.href = URL.createObjectURL(blob);
  //     link.download = fileName; // Set the downloaded file's name
  
  //     // Trigger the download
  //     link.click();
  //     toast.success(`${fileName} downloaded successfully.`, {
  //       position: "top-right",
  //       autoClose: 5000,
  //       hideProgressBar: true,
  //       closeOnClick: false,
  //       pauseOnHover: true,
  //       draggable: true,
  //       progress: undefined,
  //       theme: "light"
  //     });
  //   } catch (error) {
  //     console.error('Download failed:', error);
  //   }
  // };  

  let isPhoneErrorEmpty = () =>{
    let isAllEmpty = false;
    for(var i = 0; i<Object.keys(phoneError).length; i++){      
        if(Object.values(phoneError)[i] === "") isAllEmpty = true;
        return isAllEmpty;
    }
  }
  
  const handleValidation = (index) => {
    if (customer?.phoneNumbers[index]?.phoneNo === '') {
      setIsValidPhone((prev) => ({ ...prev, [index]: false }));
    } else {
      setIsValidPhone((prev) => ({ ...prev, [index]: true }));
    }
  };
  
  const PhoneResolve = () => {
    for(var i = 0; i< customer?.phoneNumbers?.length; i++){
      handleValidation(i);
    }
  }
    const validateFields = (step) => {
    let tempErrors = {};
    const nameRegex = /^[A-Za-z]+$/;

    PhoneResolve();
    
    if (!customer.customerType) tempErrors.customerType = 'Customer Type is required';
    
    if(customer.customerType === 'Individual'){
      if (!customer.firstName) tempErrors.firstName = 'First Name is required';
      if (!nameRegex.test(customer.firstName)) tempErrors.firstName = 'Only alphabets are accepted';
      if (!customer.lastName) tempErrors.lastName = 'Last Name is required';
      if (!nameRegex.test(customer.lastName)) tempErrors.lastName = 'Only alphabets are accepted';
    } else {
      if (!customer.companyName) tempErrors.companyName = 'Company Name is required';
    }
    
    if (Object.keys(phoneError).length > 0 && !isPhoneErrorEmpty() || (customer.phoneNumbers.length>0 && customer.phoneNumbers[0].phoneNo === '')) {
      tempErrors.phoneError = 'Phone number is required';        
    }
    
    if (!customer.email) {
      tempErrors.email = 'Email is required';
    } else if (!validateEmail(customer.email)) {
      tempErrors.email = 'Enter a valid Email';
    } 
    
    if (!customer.companyAddress_Street1) tempErrors.companyAddress_Street1 = 'Street 1 is required';      
    if (!customer.companyAddress_City) tempErrors.companyAddress_City = 'City is required';      
    if (!customer.companyAddress_State) tempErrors.companyAddress_State = 'State is required';      
    if (!customer.companyAddress_ZipCode) tempErrors.companyAddress_ZipCode = 'Zip Code is required';  
    
    if (!customer.billingAddress) {
      if(!customer.billingAddress_Street1) tempErrors.billingAddress_Street1 = 'Billing Street 1 is required';      
      if(!customer.billingAddress_City) tempErrors.billingAddress_City = 'Billing City is required';      
      if(!customer.billingAddress_State) tempErrors.billingAddress_State = 'Billing State is required';      
      if(!customer.billingAddress_ZipCode) tempErrors.billingAddress_ZipCode = 'Billing Zip Code is required';
    }

    if (customer.email && isEmailTaken(customer.email)) {
      tempErrors.email = 'This email is already taken.';
    }

    // ADD: Global phone duplicates
    (customer.phoneNumbers || []).forEach((p, idx) => {
      if (p?.phoneNo && isPhoneTaken(p.phoneNo)) {
        tempErrors.phoneError = 'This phone number is already taken.';
        setPhoneError(prev => ({ ...prev, [idx]: 'This phone number is already taken.' }));
        setIsValidPhone(prev => ({ ...prev, [idx]: false }));
      }
    });
    
    setErrors(tempErrors);    
    return Object.keys(tempErrors).length === 0;
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const normalizeEmail = (e) => (e || '').trim().toLowerCase();
  const normalizePhone = (p) => (p || '').replace(/\D/g, '');

  const isEmailTaken = (email) => {
    const e = normalizeEmail(email);
    if (!e) return false;
    return emailIndex.has(e);
  };

  const isPhoneTaken = (phone) => {
    const p = normalizePhone(phone);
    if (!p) return false;
    return phoneIndex.has(p);
  };

   const duplicateEmail = (email) => {    
    return allCustomers?.filter(x => x.email === email).length > 0;
  }

  const getFileIcon = (fileName) => {
    let icon;
    if (fileName.includes('.xlsx')) {
      icon = "/images/xlsx.png"; // Excel file
    } else if (fileName.includes('.csv')) {
      icon = "/images/csv.png"; // CSV file
    } else if (fileName.includes('jpg') || fileName.includes('jpeg') || fileName.includes('png')) {
      icon = "/images/image_placeholder.png"; // Image files
    } else if (fileName.includes('.pdf')) {
      icon = "/images/pdf_placeholder.png"; // PDF file
    } else {
      icon = "/images/placeholder.png"; // Default placeholder for unknown file types
    }
    return icon;
  };

  const handleOpenUploadDlg = () => {
    setUnsavedDialogAction({ 
      action: 'upload',
      
    });

    
    setOpenUploadDlg(true);   
  };
  
  const handleCloseUploadDlg = () => {
    if(uploadFiles.length > 0){
      handleOpenUnsavedChangesDlg();
      // MySwal.fire({
      //   title: 'Are you sure?',
      //   html: "This page has unsaved changes",
      //   showCancelButton: true,
      //   confirmButtonColor: '#23A26D',
      //   cancelButtonColor: '#d33',
      //   confirmButtonText: "Discard Changes",
      //   cancelButtonText: 'Cancel',
      // }).then((result) => {
      //   if (result.isConfirmed) {
      //     setFiles([]);
      //     setUploadFiles([]);
      //     setOpenUploadDlg(false); 
      //   }
      // });  
     }else{
        setFiles([]);
        setUploadFiles([]);
        setOpenUploadDlg(false); 
     }  
  };

  const handleDeleteTag = (tagName) => {
    setCustomer(prevCustomer => ({
        ...prevCustomer,
        tags: prevCustomer.tags.filter(tag => tag.tagName !== tagName)
    }));
  };
  const handleTagEntered = (tagName) => {

  if(tagName == '') return false;
  const lowerCaseTagName = tagName.toLowerCase();

  setCustomer((prevCustomer) => {
      
      const tagExists = prevCustomer.tags?.some(tag => tag.tagName.toLowerCase() === lowerCaseTagName);

      if (tagExists) {
          return prevCustomer; 
      }
      const updatedCustomer = {
          ...prevCustomer,
          tags: [...(prevCustomer.tags || []), { tagName }]
      };
      return updatedCustomer;
  });
  document.getElementById('TxtTag').value = '';
};

const SaveCus = async () => {
  try {                        
    const response = await SaveIndividual(customer, navigate);
    if (response && !response.isError) { 
      fetchData(customerID);
      toast.success(`Updated! Customer information successfully updated.`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        }); 
        // const timer = setTimeout(() => {          
        //   navigate('/customers');
        // }, 5000);
        // return () => clearTimeout(timer);                
    } else {
      toast.error(`Failed! ${response.result[0].failureReason}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });                 
    }
  } catch (error) {
    console.error('Error saving customer:', error);          
  }finally{
    setIsChanged(false);
    setButtonLoading(false);
  }
};

  const handleSave = ()=>{

    if(billingInfoChanged){
       const data = handleSaveBillingInfo(customerID,billingFrequencyID)
       .then((result) => {
        // Success
        if(result){
          toast.success(`Billing info updated successfully!`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
            });
            setIsChanged(false);
            setBillingInfoChanged(false)
            
        }
      })
      .catch((error) => {
        // Error
        toast.error(`Error Saving Billing Info. Please try again later!`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
        console.error('Error:', error);
        
      });
      //  console.log(data)
      return
    }
    if (validateFields(tabValue)) {        
      setButtonLoading(true);      
      SaveCus();                 
    }else{
      toast.error(`Failed! Please fill all the required fields for Customer!`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        }); 
        //setTriggerTabChange(true); 
    }
  }

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    maxSize: 2000000, // 2 MB in bytes
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
   },
    onDrop: acceptedFiles => {
      setBtnLoader(true);  
      setTimeout(() => {
        setUploadFiles(acceptedFiles.map(file => Object.assign(file)));
        setBtnLoader(false); 
      }, 1000);  // Simulating a delay for loading effect
    },
    onDropRejected: () => {
      toast.error('You can only upload 1 file with a maximum size of 2 MB.', {
       duration: 2000,
        theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });
      // setMessage('You can only upload 1 file with a maximum size of 2 MB.');
      setBtnLoader(false);
    }
  });

  // const handleTabChange = (event, newValue) => {   
    // if(isChanged){
    //   const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
    //   if (!confirmLeave) { return; } else { setTabValue(newValue); }
    // }else{
    //   setTabValue(newValue);
    // }
    // if(isChanged){
      // const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      // if (!confirmLeave) { return; } else { setTabValue(newValue); }
      // MySwal.fire({
      //   title: "Are you sure?",
      //   html: "Any unsaved changes will be discarded. Do you want to proceed?",
      //   icon: "warning",
      //   showCancelButton: true,
      //   confirmButtonColor: "#23A26D",
      //   cancelButtonColor: "#d33",
      //   confirmButtonText: "Yes, Proceed!",
      //   cancelButtonText: "Cancel",
      //   allowEscapeKey: true,
      // }).then((result) => {
      //   if (result.isConfirmed) {
      //     setTabValue(newValue);
      //   }
      // });
  //     handleOpenUnsavedChangesDlg('tabChange', newValue);
  //   }else{
  //     setTabValue(newValue);
  //   }
  // }

  const handleTabChange = (event, newValue) => {
  setTabValue(newValue);
};

  const handleNavigation = (to) => {
    // if (isChanged) {
    //   const confirmLeave = window.confirm("You have unsaved changes. Are you sure you want to leave?");
    //   if (!confirmLeave) return;
    // }
    // navigate(to);
    if(isChanged){
       handleOpenUnsavedChangesDlg('navigate');

      
      // MySwal.fire({
      //   title: "Are you sure?",
      //   html: "This page has unsaved changes",
        
      //   showCancelButton: true,
      //   confirmButtonColor: "#23A26D",
      //   cancelButtonColor: "#d33",
      //   confirmButtonText:"Discard Changes",
      //   cancelButtonText: "Cancel",
      //   allowEscapeKey: true,
      // }).then((result) => {
      //   if (result.isConfirmed) {
      //     navigate('/customers');
      //     setHasUnsavedChanges(false);
      //   }
      // });
    }else{
      navigate(to);
    }
  };
  
  const handlePageBack = () => {
    handleNavigation('/customers');    
  }

  const ALL_TYPES = [
    "Request",
    "Assessment",
    "Estimate",
    "WorkOrder",
    "Customer",
    "Invoice"
  ];
  const sortedEmployees = useMemo(() => {
    return [...(allEmployees || [])].sort((a, b) =>
      `${a.firstName ?? ""} ${a.lastName ?? ""}`
        .toLowerCase()
        .localeCompare(`${b.firstName ?? ""} ${b.lastName ?? ""}`.toLowerCase())
    );
  }, [allEmployees]);
  return (
    <UserLayout
      contentHeightFixed={contentHeightFixed}
      activeLink={"Customers"}
      showIcon={true}
      NavURL={handlePageBack}
    >
      <Card sx={{ width: "100%", height: "100%", position: 'relative' }}>
        <CardHeader
          title={
            <Box
              sx={{
                display: "flex", 
                flexDirection: { xs: "column", sm: "row" }, 
                alignItems: { xs: "flex-start", sm: "center" },
                gap: { xs: 1, sm: 2 }
              }}
            >
              <Box 
                sx={{ 
                  display: "flex", 
                  alignItems: "center",
                  flexWrap: 'wrap',
                  gap: { xs: 1, sm: 2 }
                }}
              >
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: "bold",
                    fontSize: { xs: "1.5rem", sm: "1.75rem" },
                    lineHeight: 1.2
                  }}
                >
                  {customer?.customerType === 'Individual' 
                    ? `${customer?.firstName} ${customer?.lastName}` 
                    : customer?.companyName}
                </Typography>
                <HtmlTooltip
                  title={
                    <Box sx={{ p: { xs: 2, sm: 4 } }}>
                      <Typography
                        color="text.primary"
                        fontSize="small"
                        fontWeight="600"
                        mb={2}
                      >
                        GENERAL INFORMATION
                      </Typography>
                      <Box sx={{ 
                        display: "flex", 
                        gap: { xs: 10, sm: 20 },
                        flexDirection: 'row'
                      }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <Typography color="text.secondary" fontSize="small">
                            Created On
                          </Typography>
                          <Typography color="text.secondary" fontSize="small">
                            Created By
                          </Typography>
                          <Typography color="text.secondary" fontSize="small">
                            Customer ID
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <Typography fontSize="small">
                            {customer?.createDate ? GetFormatedTimeStamp(customer?.createDate) : ''}
                          </Typography>
                          <Typography fontSize="small">                            
                            {customer?.createdBy || ''}
                          </Typography>
                          <Typography fontSize="small">
                            {customer?.customerID || ''}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  }
                >
                  <IconButton sx={{ mx: { xs: 0, sm: 2 } }} size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </HtmlTooltip>
                <CustomChipWithDropdown
                  tag={customer?.isActive ? "Active" : "Inactive"}
                  onSelect={(selectedTag) => {
                    handleChange('isActive', selectedTag)
                  }}
                  sx={{
                    ml: { xs: 0, sm: 1 },
                    mt: { xs: 1, sm: 0 }
                  }}
                />
              </Box>             
            </Box>
          }
        />
        <CardContent>
          <Box>
            <TabContext value={tabValue}>
              <TabList
                sx={{ mb: 2 }}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab
                  value="1"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          tabValue === "1" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Profile Information
                      </Box>
                    </Box>
                  }
                />
                {hasBillingGetPermission &&  (
                  <Tab
                    value="2"
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          width: "100%",
                          color: theme.palette.mode === 'dark' ? 
                            tabValue === "2" ? "#fff" : "inherit" :
                            "inherit"
                        }}
                      >
                        <Box
                          sx={{
                            fontSize: { xs: '0.875rem', md: "1rem" },
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Billing Preference
                        </Box>
                      </Box>
                    }
                  />
                )}
                <Tab
                  value="3"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          tabValue === "3" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Contacts
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="4"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          tabValue === "4" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Jobsites
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="5"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          tabValue === "5" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Attachments
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="6"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          tabValue === "6" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Notes
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="7"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          tabValue === "7" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Communication Log
                      </Box>
                    </Box>
                  }
                />
               <Tooltip 
              title="Coming Soon!" 
              arrow
              placement="top"
              sx={{
                '& .MuiTooltip-tooltip': {
                  fontSize: '14px',
                  backgroundColor: '#333',
                  color: '#fff'
                }
              }}
            >
              <span>
                <Tab
                  value="9"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          tabValue === "9" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Customer Portal
                      </Box>
                    </Box>
                  }
                />
              </span>
            </Tooltip>
              </TabList>
              <TabPanel value="1" sx={{ px: 0 }}>
                <Box sx={{ 
                  display: "flex", 
                  flexDirection: { xs: "column", md: "row" },
                  gap: { xs: 0, md: 4 }
                }}>
                  <Box sx={{ 
                    width: { xs: "100%", md: "50%" },
                    order: { xs: 1, md: 1 }
                  }}>
                    <Grid container spacing={{ xs: 3, sm: 5 }}>
                      <Grid item xs={12}>
                        <FormControl>
                          <FormLabel
                            required
                            sx={{
                              color: theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.78) !important' : "rgba(47, 43, 61, 0.78)!important",
                              '& .MuiFormLabel-asterisk': { color: 'error.main' },
                              marginBottom: '4px'
                            }}
                          >
                            Customer Type
                          </FormLabel>
                          <RadioGroup
                            row
                            aria-labelledby="customerTypeLabel"
                            name="customerTypeSelection"
                            value={customer.customerType}
                            onChange={(e) => handleChange('customerType', e.target.value)}
                            sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: { xs: 4, sm: 5 } }}
                          >
                            <FormControlLabel
                              value="Individual"
                              control={<Radio color='success' size="small"/>}
                              label="Individual"
                              sx={{ mr: { xs: 2, sm: 0 } }}
                            />
                            <FormControlLabel
                              value="Business"
                              control={<Radio color='success' size="small"/>}
                              label="Business"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                      {customer.customerType === "Individual" && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <CustomTextField
                              fullWidth
                              required
                              placeholder="First Name"
                              label="First Name"
                              value={customer?.firstName || ''}
                              onChange={(e) => handleChange('firstName', e.target.value)}
                              error={!!errors.firstName}
                              helperText={errors.firstName}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <CustomTextField
                              fullWidth
                              required
                              placeholder="Last Name"
                              label="Last Name"
                              value={customer?.lastName || ''}
                              onChange={(e) => handleChange('lastName', e.target.value)}
                              error={!!errors.lastName}
                              helperText={errors.lastName}
                              size="small"
                            />
                          </Grid>
                        </>
                      )}
                      {customer.customerType === "Business" && (
                        <Grid item xs={12} sm={6}>
                          <CustomTextField
                            fullWidth
                            required
                            placeholder="Cleri"
                            label="Company Name"
                            value={customer?.companyName || ''}
                            onChange={(e) => handleChange('companyName', e.target.value)}
                            error={!!errors.companyName}
                            helperText={errors.companyName}
                            size="small"
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          fullWidth
                          required
                          placeholder="name@example.com"
                          label={customer.customerType === "Business" ? 'Company Email' : 'Email'}
                          value={customer?.email || ''}
                          onChange={(e) => handleChange('email', e.target.value)}
                          error={!!errors.email}
                          helperText={errors.email}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                          label="Mark as Lead"
                          control={
                            <Checkbox 
                              name="basic-unchecked" 
                              color='success'
                              checked={customer.isLead}
                              onChange={(e) => handleChange('isLead', e.target.checked)}
                              size="small"
                            />
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6.01}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <FormLabel
                            required
                            sx={{
                              display: 'flex',
                              fontSize: "13px",
                              color: theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.78) !important' : "rgba(47, 43, 61, 0.78)!important",
                              '& .MuiFormLabel-asterisk': { color: 'error.main' },
                              marginBottom: '4px'
                            }}
                          >
                            {customer.customerType === "Business" ? 'Company Phone' : 'Phone'}
                          </FormLabel>
                          <Box sx={{ display: "flex", flexDirection: "column" }}>
                            {customer.phoneNumbers.map((field, index) => (
                              <Box
                                key={field.phoneID || index}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mb: 2,
                                  flexDirection: { xs: "column", sm: "row" }
                                }}
                              >
                                <Box sx={{ width: '100%' }}>
                                  <PhoneInput
                                    disableDropdown={true}
                                    disableCountryCode={true}
                                    required
                                    inputClass="custom-phone-input"
                                    placeholder="(000) 000-0000"
                                    label={`Phone # ${index + 1}`}
                                    country={'us'}
                                    onlyCountries={['us']}
                                    isValid={isValidPhone[index]}
                                    regions={'america'}
                                    value={field.phoneNo || ""}
                                    onChange={(e) => handlePhoneFieldChange(index, e)}
                                    copyNumbersOnly={true}
                                    jumpCursorToEnd={false}
                                    containerStyle={{
                                      width: '100%',
                                      margin: 0,
                                    }}
                                    inputStyle={{
                                      width: '100%',
                                      height: '39px',
                                      border: !isValidPhone[index] ? '1px solid #EA5455' : '1px solid rgba(208, 212, 241, 0.2)',
                                      borderRadius: '6px',
                                      paddingLeft: '48px',
                                      backgroundColor: theme.palette.mode === 'dark' ? "#1B2431" : "#fff",
                                      color: theme.palette.mode === 'dark' ? "rgba(208, 212, 241, 0.68)" : "rgba(47, 43, 61, 0.68)",
                                    }}
                                  />
                                  {phoneError[index] && (
                                    <FormHelperText sx={{ color: '#EA5455', mt: '2px', mb: '6px', lineHeight: 1.154 }}>
                                      {phoneError[index]}
                                    </FormHelperText>
                                  )}
                                </Box>
                                <Box sx={{ 
                                  display: 'flex', 
                                  gap: 1,
                                  mt: { xs: 1, sm: 0 },
                                  width: { xs: '100%', sm: 'auto' },
                                  justifyContent: { xs: 'flex-end', sm: 'flex-start' }
                                }}>
                                  <IconButton
                                    title="Mark as Primary"
                                    color="secondary"
                                    onClick={() => handleSetPrimary(index)}
                                    disabled={field.isPrimary}
                                  >
                                    {field.isPrimary ? (
                                      <StarIcon sx={{ color: '#E7D557' }} />
                                    ) : (
                                      <StarOutlineIcon />
                                    )}
                                  </IconButton>
                                  <IconButton
                                    title="Delete"
                                    color="secondary"
                                    onClick={() => handleDeletePhoneField(index)}
                                    disabled={field.isPrimary}
                                  >
                                    <Icon icon="tabler:trash" />
                                  </IconButton>
                                </Box>
                              </Box>
                            ))}
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={handleAddPhoneField}
                              size={ smBelow ? "small" : "medium"}
                              sx={{ mt: 1, alignSelf: 'flex-start' }}
                            >
                              Add Phone
                            </Button>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={12} md={6}>
                        <CustomAutocomplete
                         options={[...timezones].sort((a, b) =>
                           a.label.localeCompare(b.label)
                              )}
                          value={timezones.find(option => option.value === customer?.timeZone) || null}
                          onChange={(event, newValue) => handleChange('timeZone', newValue?.value || '')}
                          getOptionLabel={(option) => option?.label || ''}
                          renderInput={(params) => (
                            <CustomTextField 
                              fullWidth 
                              label='Timezone' 
                              placeholder='EST - Eastern Standard Time' 
                              {...params} 
                              size="small"
                            />
                          )}
                        />
                      </Grid>
                    <Grid item xs={12} sm={12} md={6}>
                      <CustomAutocomplete
                        options={[...allCustomers].sort((a, b) =>
                          (
                            a.firstName || a.lastName
                              ? `${a.firstName ?? ""} ${a.lastName ?? ""}`
                              : a.companyName ?? ""
                          )
                            .toLowerCase()
                            .localeCompare(
                              (
                                b.firstName || b.lastName
                                  ? `${b.firstName ?? ""} ${b.lastName ?? ""}`
                                  : b.companyName ?? ""
                              ).toLowerCase()
                            )
                        )}
                        value={
                          allCustomers.find(
                            (option) => option.customerID === customer?.referredBy
                          ) || null
                        }
                        onChange={(event, newValue) =>
                          handleChange("referredBy", newValue?.customerID || null)
                        }
                        getOptionLabel={(option) =>
                          option?.firstName || option?.lastName
                            ? `${option?.firstName ?? ""} ${option?.lastName ?? ""}`.trim()
                            : option?.companyName ?? ""
                        }
                        isOptionEqualToValue={(option, value) =>
                          option?.customerID === value?.customerID
                        }
                        renderInput={(params) => (
                          <CustomTextField
                            label="Referred By"
                            fullWidth
                            placeholder="Search or Select Referral"
                            {...params}
                          />
                        )}
                      />
                    </Grid>
                  <Grid item xs={12} sm={6}>
                      <Autocomplete
                        options={sortedEmployees}
                        value={
                          sortedEmployees.find(
                            (option) => option.employeeID === customer?.soldBy
                          ) || null
                        }
                        onChange={(event, newValue) =>
                          handleChange("soldBy", newValue?.employeeID || null)
                        }
                        getOptionLabel={(option) =>
                          option?.firstName || option?.lastName
                            ? `${option?.firstName ?? ""} ${option?.lastName ?? ""}`.trim()
                            : ""
                        }
                        isOptionEqualToValue={(option, value) =>
                          option?.employeeID === value?.employeeID
                        }
                        disableListWrap
                        ListboxProps={{
                          style: { maxHeight: 300 },
                        }}
                        renderInput={(params) => (
                          <CustomTextField
                            label="Sold By"
                            fullWidth
                            placeholder="Search or Select Employee"
                            {...params}
                          />
                        )}
                      />
                    </Grid>                 
                      <Grid item xs={12} sm={12} md={6}>
                        <CustomTextField
                          fullWidth
                          placeholder="Type a tag & hit Enter to add it."
                          label="Tags"
                          id="TxtTag"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleTagEntered(e.target.value);
                            }
                          }}
                          size="small"
                        />
                      </Grid>
                        <Grid item xs={12}  >
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', flexDirection: 'row', gap: 2 }}>
                      {(customer?.tags || []).map((tag, index) => (
                      
                        <CustomChip
                                key={index}
                                rounded
                                size="small"
                                skin="light"
                                sx={{
                                  lineHeight: 1,
                                  paddingTop: "13px",
                                  paddingRight: "0 !important",
                                  paddingBottom: "13px",
                                  borderRadius: "12px !important",
                                  fontSize: "medium",
                                  border: "1px solid",
                                  // m: 0.5
                                }}
                                color="info"
                                label={
                                  <Box sx={{ display: "flex", alignItems: "center", pr: 0 }}>
                                    {tag.tagName}
                                    <IconButton
                                      size="small"
                                      sx={{ ml: 1 }}
                                      onClick={() => handleDeleteTag(tag.tagName)}
                                    >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            }
                          />
                         
                      ))}
                       </Box>
                        </Grid>
                    </Grid>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      mt: { xs: 4, md: 0 },
                      pt: { xs: 4, md: 0 },
                      px: { xs: 0, md: 4}, 
                      borderLeft: { xs: "none", md: "1px solid #E1E4EA" },
                      borderTop: { xs: "1px solid #E1E4EA", md: "none" },
                      width: { xs: "100%", md: "50%" },
                      order: { xs: 2, md: 2 }
                    }}
                  >
                    <Grid container spacing={{ xs: 3, sm: 5 }}>
                    <Grid item xs={12}>
                        <Divider
                          sx={{
                            color: "text.disabled",
                            "& .MuiDivider-wrapper": { px: { xs: 0, sm: 6 } },
                            fontSize: theme.typography.body2.fontSize,
                            my: (theme) => `${theme.spacing(1)} !important`,
                          }}
                        >
                          {customer.customerType === "Business" ? 'Company Address' : 'Main Address'}
                        </Divider>
                      </Grid>

                      <Grid item xs={12}>
                        <CustomTextField
                          fullWidth
                          placeholder="Street 1"                          
                          value={customer?.companyAddress_Street1 || '' }
                          onChange={(e) => handleChange('companyAddress_Street1', e.target.value)}
                          error={!!errors.companyAddress_Street1}
                          helperText={errors.companyAddress_Street1}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CustomTextField
                          fullWidth
                          placeholder="Street 2"
                          value={customer?.companyAddress_Street2 || ''}
                          onChange={(e) => handleChange('companyAddress_Street2', e.target.value)}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          fullWidth
                          placeholder="City"
                          value={customer?.companyAddress_City || ''}
                          onChange={(e) => handleChange('companyAddress_City', e.target.value)}
                          error={!!errors.companyAddress_City}
                          helperText={errors.companyAddress_City}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                       <CustomAutocomplete
                      options={stateNames}
                      id="autocomplete-custom"
                      value={stateNames.find(option => option === customer?.companyAddress_State) || null}
                      onInputChange={(event, newInputValue) => {
                        // auto expand abbreviations
                        const expanded = expandStateName(newInputValue);
                        setStateInput(expanded); // update local input
                        handleChange("companyAddress_State", expanded); // update state
                      }}
                      onChange={(event, newValue) => handleChange("companyAddress_State", newValue)}
                      getOptionLabel={(option) => option || ""}
                      renderInput={(params) => (
                        <CustomTextField
                          {...params}
                          fullWidth
                          placeholder="State"
                          size="small"
                          error={!!errors.companyAddress_State}
                          helperText={errors.companyAddress_State}
                        />
                      )}
                    />

                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          fullWidth
                          placeholder="Zip code (Mandatory)"
                          value={customer?.companyAddress_ZipCode || ''}
                          onChange={(e) => handleChange('companyAddress_ZipCode', e.target.value)}
                          error={!!errors.companyAddress_ZipCode}
                          helperText={errors.companyAddress_ZipCode}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          fullWidth
                          placeholder="County"
                          value={customer?.companyAddress_County || ''}
                          onChange={(e) => handleChange('companyAddress_County', e.target.value)}
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormControlLabel
                          label={`Billing address is the same as ${customer.customerType === "Business" ? 'Company Address' : 'Main Address'}`}
                          control={
                            <Checkbox
                              checked={customer.billingAddress}
                              onChange={(e) => handleChange('billingAddress', e.target.checked)}
                              name="basic-unchecked"
                              color="success"
                              size="small"
                            />
                          }
                        />
                      </Grid>

                      {/* Billing Address Section (if different) */}
                      {!customer.billingAddress && (
                        <>
                          <Grid item xs={12}>
                            <Divider
                              sx={{
                                color: "text.disabled",
                                "& .MuiDivider-wrapper": { px: { xs: 0, sm: 6 } },
                                fontSize: theme.typography.body2.fontSize,
                                my: (theme) => `${theme.spacing(3)} !important`,
                              }}
                            >
                              Billing Address
                            </Divider>
                          </Grid>
                          <Grid item xs={12}>
                            <CustomTextField
                              fullWidth
                              placeholder="Street 1"
                              value={customer?.billingAddress_Street1}
                              onChange={(e) => handleChange('billingAddress_Street1', e.target.value)}
                               error={!!errors.billingAddress_Street1}
                              helperText={errors.billingAddress_Street1}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <CustomTextField
                              fullWidth
                              placeholder="Street 2"
                              value={customer?.billingAddress_Street2}
                              onChange={(e) => handleChange('billingAddress_Street2', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <CustomTextField
                              fullWidth
                              placeholder="City"
                              value={customer?.billingAddress_City}
                              onChange={(e) => handleChange('billingAddress_City', e.target.value)}
                              size="small"
                              error={!!errors.billingAddress_City}
                              helperText={errors.billingAddress_City}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <CustomAutocomplete
                            options={stateNames}
                            id="autocomplete-custom"
                            value={stateNames.find(option => option === customer?.billingAddress_State) || null}
                            onInputChange={(event, newInputValue) => {
                              const expanded = expandStateName(newInputValue);
                              setStateInput(expanded); // local input
                              handleChange("billingAddress_State", expanded); 
                            }}
                            onChange={(event, newValue) => handleChange("billingAddress_State", newValue)}
                            getOptionLabel={(option) => option || ""}
                            renderInput={(params) => (
                              <CustomTextField
                                {...params}
                                 error={!!errors.billingAddress_State}
                                helperText={errors.billingAddress_State}
                                fullWidth
                                placeholder="State"
                                size="small"
                              />
                            )}
                          />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <CustomTextField
                              fullWidth
                              placeholder="Zip code"
                              value={customer?.billingAddress_ZipCode}
                              onChange={(e) => handleChange('billingAddress_ZipCode', e.target.value)}
                              error={!!errors.billingAddress_ZipCode}
                              helperText={errors.billingAddress_ZipCode}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <CustomTextField
                              fullWidth
                              placeholder="County"
                              value={customer?.billingAddress_County}
                              onChange={(e) => handleChange('billingAddress_County', e.target.value)}
                              size="small"
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>
                </Box>
              </TabPanel>
              {hasBillingGetPermission && (
                <TabPanel value="2" sx={{ px: 0, maxWidth: { md: '60%', xs: '100%' }, pointerEvents: !hasBillingSavePermission ? 'none' : 'default'  }}>
                  <BillingInformation
                    // estTravelTime={estTravelTime}
                    // setEstTravelTime={setEstTravelTime}
                    billingFreq={billingFreq}
                    recurringType={recurringType}                  
                    depositReq={depositReq}
                    setDepositReq={setDepositReq}
                    depositType={depositType}
                    setDepositType={setDepositType}
                    formatsBilling={formatsBilling}
                    setFormatsBilling={setFormatsBilling}
                    monthlyOptions={monthlyOptions}        
                    setMonthlyOptions={setMonthlyOptions}
                    // occurrences={occurrences}
                    // setOccurrences={setOccurrences}
                    cycleStartDate={cycleStartDate}
                    setCycleStartDate={setCycleStartDate}
                    intervalDaysInput={intervalDaysInput}
                    setIntervalDaysInput={setIntervalDaysInput}
                    weekOfMonth={weekOfMonth}
                    setWeekOfMonth={setWeekOfMonth}
                    quarter={quarter}
                    setQuarter={setQuarter}
                    monthOfQuarter={monthOfQuarter}
                    setMonthOfQuarter={setMonthOfQuarter}
                    // handleEstTravelTimeChange={handleEstTravelTimeChange}
                    handleBillingFreqChange={handleBillingFreqChange}
                    handleRecurringTypeChange={handleRecurringTypeChange}
                    handledDepositReqChange={handledDepositReqChange}
                    handleDepositTypeChange={handleDepositTypeChange}
                    handleFormatBilling={handleFormatBilling}
                    formatsBillingWeekly={formatsBillingWeekly}
                    setIsChanged={setIsChanged}
                    setBillingInfoChanged={setBillingInfoChanged}
                    
                  />
                </TabPanel>
              )}
              <TabPanel value="3" sx={{ px: 0 }}>
                {/* {console.log(allContacts)} */}
                <Grid container spacing={{ xs: 3, sm: 5 }}>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' }, 
                      alignItems: { xs: 'stretch', sm: 'center' },
                      justifyContent: 'flex-end',
                      gap: 2,
                      width: '100%'
                    }}>
                      <CustomTextField
                        placeholder='Search Contact'
                        disabled={!hasContactGetPermission}
                        value={searchContactText}
                        fullWidth
                        sx={{
                          width: { xs: '100%', sm: 'auto' },
                          minWidth: { xs: 'unset', sm: '250px' }
                        }}
                        {...(hasContactGetPermission && {
                          onChange: (e) => handleContactSearchChange(e.target.value)
                        })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <Icon icon='tabler:search' fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              {searchContactText && (
                                <IconButton
                                  edge="end"
                                  onClick={handleClearContactSearch}
                                  aria-label="clear"
                                  size="small"
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 2,
                        width: { xs: '100%', sm: 'auto' },
                        flexDirection: { xs: 'column', sm: 'row' }
                      }}>
                        {/* <Button 
                          variant='outlined'
                          color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
                          disabled={!hasContactSavePermission}
                          size={ smBelow ? "small" : "medium" }
                          fullWidth={true}
                          sx={{
                            whiteSpace: 'nowrap',
                            width: { xs: '100%', sm: 'auto' }
                          }}
                          {...(hasContactSavePermission && {
                            onClick: ()=> handleOpenBulkImportContactDlg() 
                          })}
                        >
                          Bulk Import
                        </Button> */}
                        
                        <Button 
                          variant='contained' 
                          color='main' 
                          startIcon={<AddIcon fontSize="small" />}
                          disabled={!hasContactSavePermission}
                          size={ smBelow ? "small" : "medium" }
                          fullWidth={true}
                          sx={{
                            whiteSpace: 'nowrap',
                            width: { xs: '100%', sm: 'auto' }
                          }}
                          {...(hasContactSavePermission && {
                            onClick: ()=> handleOpenNewContactDlg() 
                          })}
                        >
                          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                            Create Contact
                          </Box>
                          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                            Create
                          </Box>
                        </Button>                    
                        {/* {console.log(allContacts)}     */}
                        <ImportExportOptions 
                          handleOpenBulkImportDlg={handleOpenBulkImportContactDlg}
                          data={allContacts}
                          module="Contacts"
                          customerID={customerID}
                        />
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ 
                      width: '100%',
                      overflowX: 'auto',
                      '& .MuiDataGrid-root': {
                        minWidth: { xs: '800px', sm: '100%' }
                      }
                    }}>
                      <DataGridPro
                        autoHeight
                        rowSelection={false}
                        rows={filteredContactRows}
                        rowHeight={62}
                        columns={columnsAddContact.map(column => ({
                          ...column,
                          flex: 1,
                          minWidth: 150,
                          headerClassName: 'data-grid-header'
                        }))}
                        slots={{
                          footer: () => null
                        }}
                        // pageSizeOptions={[5, 10, { value: filteredContactRows.length, label: 'Show All'}]}
                        // paginationModel={paginationModelAddContact}
                        // onPaginationModelChange={setPaginationModelAddContact}
                        columnVisibilityModel={{
                          id: false,
                        }}
                        sx={{
                          "& .MuiDataGrid-columnHeaderTitle": {
                            textTransform: "none",
                            fontSize: { xs: '0.75rem', sm: '0.8125rem' }
                          },
                          "& .MuiDataGrid-cell": {
                            fontSize: { xs: '0.75rem', sm: '0.8125rem' }
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </TabPanel>
              <TabPanel value="4" sx={{ px: 0 }}>
                <Grid container spacing={{ xs: 3, sm: 5 }} sx={{ px: 0 }}>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' }, 
                      alignItems: { xs: 'stretch', sm: 'center' },
                      justifyContent: 'flex-end',
                      gap: 2,
                      width: '100%'
                    }}>
                      {/* Search Field - Full width on mobile */}
                      <CustomTextField
                        placeholder='Search Jobsite'
                        disabled={!hasJobsiteGetPermission}
                        value={searchJobsiteText}
                        fullWidth
                        sx={{
                          width: { xs: '100%', sm: 'auto' },
                          minWidth: { xs: 'unset', sm: '250px' }
                        }}
                        {...(hasJobsiteGetPermission && {
                          onChange: (e) => handleJobsiteSearchChange(e.target.value)
                        })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <Icon icon='tabler:search' fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              {searchJobsiteText && (
                                <IconButton
                                  edge="end"
                                  onClick={handleClearJobsiteSearch}
                                  aria-label="clear"
                                  size="small"
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />
                      
                      {/* Buttons - Stack vertically on mobile */}
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 2,
                        width: { xs: '100%', sm: 'auto' },
                        flexDirection: { xs: 'column', sm: 'row' }
                      }}>
                        <Button 
                          variant='outlined'
                          color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
                          disabled={!hasJobsiteImportPermission}
                          size={ smBelow ? "small" : "medium"}
                          fullWidth={true}
                          sx={{
                            whiteSpace: 'nowrap',
                            width: { xs: '100%', sm: 'auto' }
                          }}
                          {...(hasJobsiteImportPermission && {
                            onClick: ()=> handleOpenBulkImportJobsiteDlg()
                          })}
                        >
                          Bulk Import
                        </Button>
                        
                        <Button 
                          variant='contained' 
                          color='main' 
                          startIcon={<AddIcon fontSize="small" />}
                          disabled={!hasJobsiteSavePermission}
                          size={ smBelow ? "small" : "medium"}
                          fullWidth={true}
                          sx={{
                            whiteSpace: 'nowrap',
                            width: { xs: '100%', sm: 'auto' }
                          }}
                          {...(hasJobsiteSavePermission && {
                            onClick: () => handleOpenNewJobsiteDlg()
                          })}
                        >
                          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                            Create Jobsite
                          </Box>
                          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                            Create
                          </Box>
                        </Button>
                          <ImportExportOptions 
                            handleOpenBulkImportDlg={handleOpenBulkImportJobsiteDlg}
                            // handleExcelExport={() => handleExcelExportByModule(allJobsites, "Jobsites")}
                            // handleCSVExport={() => handleCSVExportByModule(allJobsites, "Jobsites")}
                            data={allJobsites}
                            module="Jobsites"
                            customerID={customerID}
                          />
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={7}>
                    {hasJobsiteGetPermission && allJobsites && allJobsites.length > 0 ? (
                      <List sx={{ width: '100%' }}>
                        {allJobsites
                          .filter(row =>
                            Object.values(row).some(value =>
                              value && value.toString().toLowerCase().includes(searchJobsiteText.toLowerCase()))
                          )
                          .map((jobsite) => (
                            <ListItem
                              key={jobsite.jobsiteID}
                              sx={{ 
                                borderBottom: '1px solid', 
                                borderColor: '#D9D9D9', 
                                mb: 4,
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                justifyContent: 'space-between',
                                gap: { xs: 2, sm: 0 }
                              }}
                            >
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                width: { xs: '100%', sm: 'auto' }
                              }}>
                               <ListItemIcon color='error' sx={{ minWidth: '36px' }}>
                                <CorporateFareIcon sx={{ fontSize: smBelow ? '28px' : '32px' }} />
                                 </ListItemIcon>
                                <ListItemText 
                                  sx={{ 
                                    fontWeight: 550, 
                                    ml: 1
                                  }}
                                  primary={
                                    <Typography variant="h6" fontWeight={550}>{jobsite.jobsiteName}</Typography>
                                  } 
                                 secondary={
                          <div>
                            <div>{jobsite.street1}</div>
                            <div>{jobsite.street2}</div>
                          </div>
                        }      
                                />
                              </Box>
                              
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: { xs: 'space-between', sm: 'flex-end' },
                                mt: { xs: 1, sm: 0 }
                              }}>
                                <CustomChip 
                                  label={jobsite?.jobsiteType} 
                                  skin='light' 
                                  color={jobsite.jobsiteType === 'Residential' ? 'info' : 'warning'} 
                                  sx={{ 
                                    mr: 2,
                                    fontSize: { xs: '0.7rem', sm: '0.8125rem' }
                                  }} 
                                />
                                
                                <Box>
                                  <IconButton 
                                    onClick={()=>handleQuickViewOpen(jobsite.jobsiteID)}
                                    size={ smBelow ? "small" : "medium"}
                                  >
                                    <Icon icon='tabler:eye' fontSize={ smBelow ? "1rem" : "1.5rem"} />
                                  </IconButton>
                                  <IconButton 
                                    disabled={!hasJobsiteSavePermission}
                                    size={ smBelow ? "small" : "medium"}
                                    {...(hasJobsiteSavePermission && {
                                      onClick: ()=> handleJobsiteEdit(jobsite.jobsiteID)
                                    })}
                                  >
                                    <Icon icon='tabler:pencil' fontSize={ smBelow ? "1rem" : "1.5rem"} />
                                  </IconButton>
                                  <IconButton 
                                    color='error'
                                    disabled={!hasJobsiteDeletePermission}
                                    size={ smBelow ? "small" : "medium"}
                                    {...(hasJobsiteDeletePermission && {
                                      onClick: ()=> handleJobsiteDelete(jobsite.jobsiteID,jobsite.jobsiteName)
                                    })}
                                  >
                                    <Icon icon='tabler:trash' fontSize={ smBelow ? "1rem" : "1.5rem"} />
                                  </IconButton>
                                </Box>
                              </Box>
                            </ListItem>
                          ))}
                      </List>
                    ) : (
                      <Card>
                        <CardContent
                          sx={{
                            display: "flex",
                            textAlign: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            backgroundColor: "#FBFBFC",
                            p: (theme) => `${theme.spacing(6, 3, 5)} !important`,
                          }}
                        >
                          <CustomAvatar skin="light" sx={{ mb: 2, width: 50, height: 50 }}>
                            <CorporateFareIcon fontSize="small" />
                          </CustomAvatar>
                          <Typography variant="h4" sx={{ mb: 2, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                            No Jobsites added yet
                          </Typography>
                          <Typography sx={{ mb: 4, color: "text.secondary", fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Add jobsites where services will be requested in the future.
                          </Typography>
                        </CardContent>
                      </Card>
                    )}
                  </Grid>
                </Grid>
              </TabPanel>
              <TabPanel value="5">
               <Box sx={{ 
                  display: "flex", 
                  flexDirection: { xs: 'column', sm: 'row' }, 
                  justifyContent: 'flex-end',
                  gap: 2,
                  alignItems: { xs: 'stretch', sm: 'center' } 
                }}>
                <CustomTextField
                      select
                      label=""
                      defaultValue="selectAll"
                      value={ddlArray}
                      SelectProps={{
                        multiple: true,
                        displayEmpty: true,
                        renderValue: () => "Show all",
                      }}
                    >
                      <MenuItem value="selectAll">
                        <FormControlLabel
                          label="Select All"
                          control={<Checkbox name="basic-unchecked" />}
                        />
                      </MenuItem>
                      <MenuItem value="requests">
                        <FormControlLabel
                          label="Requests"
                          control={<Checkbox name="basic-unchecked" />}
                        />
                      </MenuItem>
                      <MenuItem value="assessments">
                        <FormControlLabel
                          label="Assessments"
                          control={<Checkbox name="basic-unchecked" />}
                        />
                      </MenuItem>
                      <MenuItem value="estimates">
                        <FormControlLabel
                          label="Estimates"
                          control={<Checkbox name="basic-unchecked" />}
                        />
                      </MenuItem>
                      <MenuItem value="jobOrders">
                        <FormControlLabel
                          label="Job Orders"
                          control={<Checkbox name="basic-unchecked" />}
                        />
                      </MenuItem>
                      <MenuItem value="invoices">
                        <FormControlLabel
                          label="Invoices"
                          control={<Checkbox name="basic-unchecked" />}
                        />
                      </MenuItem>
                    </CustomTextField>
                    <DateRangeCalendar />
                    <Button
                      onClick = {handleOpenUploadDlg}
                      variant="contained"
                      color='main'
                      startIcon={<AddIcon />}
                    >
                      Add Attachment
                    </Button>
                </Box>
                <Divider
                  sx={{
                    color: "text.disabled",
                    "& .MuiDivider-wrapper": { px: { xs: 0, sm: 6 } },
                    fontSize: theme.typography.body2.fontSize,
                    my: (theme) => `${theme.spacing(3)} !important`,
                  }}
                />
                <Box>
                  {isAttachmentsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <Timeline sx={{ p: 0 }}>
                      {attachments.length === 0 ? (
                         <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mx: "auto",
                    border: "1px solid #EAEAEA",
                    borderRadius: 1,
                    width: "100%",
                    height: 280,
                    py: 4,
                    alignItems: "center" ,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      justifyContent: "center",
                      alignItems: "center",
                      width: { xs: "90%", sm: "100%" }, 
                      maxWidth: 400, 
                      px: 2,
                      textAlign: "center" 
                    }}
                  >
                <img
                  src={AddEmployeeDoodle}
                  alt="Empty State"
                  style={{ width: "100px", height: "auto" }}
                />
                <Typography variant="h6">No attachments added yet</Typography>
              </Box>
            </Box>
                      ) : (
                        Object.entries(groupedAttachments).map(([date, items]) => (
                          <React.Fragment key={date}>
                            <Typography variant="h5" sx={{ 
                              fontWeight: "bold",
                              fontSize: { xs: '1.125rem', sm: '1.25rem' },
                              mb: 2
                            }}>
                              {date}
                            </Typography>
                            
                            {items.map((attachment, index) => {
                              const parsedDate = new Date(attachment.createDate);
                              const formattedTime = !isNaN(parsedDate.getTime()) ? format(parsedDate, 'hh:mm a') : "Unknown time";
                              
                              const deleteAttachment = async (event) => {
                                event.stopPropagation();
                                // Your existing delete logic
                              };

                              return (
                                <TimelineItem key={attachment.mediaID || index}>
                                  <TimelineSeparator>
                                    <TimelineDot color="error" sx={{ width: 12, height: 12 }} />
                                    {index < items.length - 1 && <TimelineConnector sx={{ height: '40px' }} />}
                                  </TimelineSeparator>
                                  
                                  <TimelineContent sx={{ 
                                    py: { xs: 1, sm: 2 },
                                    "& svg": { 
                                      verticalAlign: "middle", 
                                      mx: { xs: 1, sm: 2 },
                                      fontSize: { xs: '1rem', sm: '1.25rem' }
                                    }
                                  }}>
                                    <Box sx={{ 
                                      mb: 1, 
                                      display: "flex", 
                                      flexWrap: "wrap", 
                                      alignItems: "center",
                                      gap: { xs: 1, sm: 2 }
                                    }}>
                                      {attachment.requestID ? (
                                        <Typography sx={{ mr: 1 }} variant="body1">
                                          Attached with Request ID{" "}
                                          <Box component="span" sx={{ 
                                            color: "#007AFF", 
                                            textDecoration: "underline",
                                            fontWeight: 500
                                          }}>
                                            {attachment.requestID || "REQ001"}
                                          </Box>
                                        </Typography>
                                      ) : (
                                        <Typography sx={{ mr: 1 }} variant="body1">
                                          Attached by {customer?.companyName ? customer?.companyName : `${customer?.firstName} ${customer?.lastName}`}
                                        </Typography>
                                      )}
                                    </Box>
                                    
                                    <Typography variant="caption" sx={{ 
                                      color: "text.secondary", 
                                      fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                      display: 'block',
                                      mb: 1
                                    }}>
                                      {formattedTime} - {customer?.companyName ? customer?.companyName : `${customer?.firstName} ${customer?.lastName}`}
                                    </Typography>
                                    
                                    <Box
                                      sx={{ 
                                        px: { xs: 2, sm: 4 }, 
                                        py: { xs: 2, sm: 3 }, 
                                        mt: 1, 
                                        display: "flex", 
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        border: '1px solid #D9D9D9', 
                                        borderRadius: 1,
                                        width: { xs: '100%', sm: '80%', md: '60%' },
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        gap: { xs: 2, sm: 0 }
                                      }}
                                    >
                                      <Box sx={{ 
                                        display: "flex", 
                                        alignItems: "center",
                                        width: { xs: '100%', sm: 'auto' }
                                      }}>
                                        <img
                                          width={40}
                                          height={40}
                                          alt={attachment.fileName || "attachment"}
                                          loading="lazy"
                                          src={thumbnails[attachment.fileName] || '/images/ImageLoader.gif'}
                                          style={{ objectFit: 'cover' }}
                                        />
                                        <Typography variant="body2" sx={{ 
                                          ml: 2,
                                          fontSize: { xs: '0.875rem', sm: '1rem' },
                                          wordBreak: 'break-word'
                                        }}>
                                          {attachment.fileName || ""}
                                        </Typography>
                                      </Box>
                                      
                                      <OptionsMenu
                                        iconButtonProps={{
                                          size: 'small',
                                          sx: {
                                            color: 'text.secondary',
                                            p: 0.3,
                                            borderRadius: '50%',
                                            '&:hover': {
                                              backgroundColor: theme => theme.palette.action.hover,
                                              borderRadius: '50%',
                                            },
                                          },
                                        }}
                                        options={[
                                          {
                                            text: (
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Icon icon="tabler:download" fontSize="1rem"/>
                                                Download
                                              </Box>
                                            ),
                                            menuItemProps: {
                                              onClick: () => downloadAttachment(customer.customerID, attachment.fileName),
                                              sx: {
                                                color: 'primary.main',
                                                '&:not(.Mui-focusVisible):hover': {
                                                  color: 'primary.main',
                                                  backgroundColor: theme => hexToRGBA(theme.palette.primary.main, 0.08),
                                                }
                                              }
                                            }
                                          },
                                          {
                                            text: (
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Icon icon="tabler:trash" fontSize="1rem"/>
                                                Delete
                                              </Box>
                                            ),
                                            menuItemProps: {
                                              onClick: deleteAttachment,
                                              sx: {
                                                color: 'error.main',
                                                '&:not(.Mui-focusVisible):hover': {
                                                  color: 'error.main',
                                                  backgroundColor: theme => hexToRGBA(theme.palette.error.main, 0.08),
                                                }
                                              }
                                            },                                          
                                          }
                                        ]}
                                      />
                                    </Box>
                                  </TimelineContent>
                                </TimelineItem>
                              )
                            })}
                          </React.Fragment>
                        ))
                      )}
                    </Timeline>
                  )}
                </Box>
              </TabPanel>
              <TabPanel value="6" sx={{ px: 0 }}>
                <Box sx={{
                  height: '70vh',
                }}>
                  <NotesComponent
                    customerID={customerID}
                    requestID={customerID} // assuming for customer notes you're using customerID as entityID
                    requestType="Customer"
                    setLoading={setLoading}
                    rules={{}} // If you have rules for notes permissions or filters
                  />
                </Box>
              </TabPanel>
              <TabPanel value="7" sx={{ px: { xs: 0, sm: 0 } }}>
                <Box sx={{ 
                  display: "flex", 
                  flexDirection: { xs: 'column', sm: 'row' }, 
                  justifyContent: "flex-end", 
                  alignItems: { xs: 'stretch', sm: 'center' }, 
                  gap: 2,
                  mb: { xs: 2, sm: 0 }
                }}>
                  <CustomTextField
                    placeholder="Search Log"
                    disabled={!hasCommunicationGetPermission}
                    value={searchCommLogText}
                    onChange={e => setSearchCommLogText(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{
                      width: { xs: '100%', sm: '200px' },
                      order: { xs: 1, sm: 1 }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon icon='tabler:search' fontSize={ smBelow ? "small" : "medium"} />
                        </InputAdornment>
                      ),
                    }}
                  />

                    <CustomTextField
                      select
                      fullWidth
                      value={selectedTypes}
                      size="small"
                      onChange={e => {
                        // 1) pull out what MUI gave us
                        let v = e.target.value;
                        // 2) if it's a string (happens on single-toggles/autofill), split into an array
                        const arr = typeof v === "string" ? v.split(",") : v;
                        // 3) handle the special “all” toggle
                        if (arr.includes("all")) {
                          setSelectedTypes(
                            selectedTypes.length === ALL_TYPES.length ? [] : ALL_TYPES
                          );
                        } else {
                          setSelectedTypes(arr);
                        }
                      }}
                      sx={{
                        width: { xs: '100%', sm: '200px' },
                        // order: { xs: 3, sm: 3 }
                        // flex: '0 0 200px',
                      }}
                      disabled={!hasCommunicationGetPermission}
                      SelectProps={{
                        multiple: true,
                        displayEmpty: true,
                        renderValue: selected => {
                          // normalize here too
                          const vals = typeof selected === "string" ? selected.split(",") : selected;
                          // return vals.length ? vals.join(", ") : "Show all";
                          return vals.length > 0 
                          ? `${vals.length} type(s) selected` 
                          : "Record Type"
                        },
                        // renderValue: () => "Record Type",
                        MenuProps: {
                          PaperProps: { style: { maxHeight: 4.5 * 48 + 8, width: 200 } }
                        }
                      }}
                    >
                      <MenuItem value="all">
                        <Checkbox checked={selectedTypes.length === ALL_TYPES.length} />
                        Show All
                      </MenuItem>
                      <MenuItem value="Request">
                        <Checkbox checked={selectedTypes.includes("Request")} />
                        Requests
                      </MenuItem>
                      <MenuItem value="Assessment">
                        <Checkbox checked={selectedTypes.includes("Assessment")} />
                        Assessments
                      </MenuItem>
                      <MenuItem value="Estimate">
                        <Checkbox checked={selectedTypes.includes("Estimate")} />
                        Estimates
                      </MenuItem>
                      <MenuItem value="WorkOrder">
                        <Checkbox checked={selectedTypes.includes("WorkOrder")} />
                        Work Orders
                      </MenuItem>
                      <MenuItem value="Customer">
                        <Checkbox checked={selectedTypes.includes("Customer")} />
                        Customer
                      </MenuItem>
                      <MenuItem value="Invoice">
                        <Checkbox checked={selectedTypes.includes("Invoice")} />
                        Invoices
                      </MenuItem>
                    </CustomTextField>

                  <DateRangeCalendar 
                    initialStartDate={dayjs(formatDateSubtractDays(new Date(), 90))} 
                    initialEndDate={dayjs(formatDateSubtractDays(new Date(), 0))}
                    onDateChange={fetchLogsWithDateRange}
                    disabled={hasCommunicationGetPermission}
                    hasCommunicationGetPermission={hasCommunicationGetPermission}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      order: { xs: 4, sm: 3 }
                    }}
                  />

                  <Button
                    variant="contained"
                    color='main'
                    startIcon={<AddIcon fontSize={ smBelow ? "small" : "medium"} />}
                    disabled={!hasCommunicationSavePermission}
                    size={ smBelow ? "small" : "medium"}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      order: { xs: 3, sm: 4 },
                      whiteSpace: 'nowrap'
                    }}
                    {...(hasCommunicationSavePermission && {
                      onClick: () => handleOpenNewComLogDlg()
                    })}
                  >
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'inline' } }}>
                      New Communication
                    </Box>
                  </Button>
                </Box>
                <Divider
                  sx={{
                    color: "text.disabled",
                    "& .MuiDivider-wrapper": { px: { xs: 0, sm: 6 } },
                    fontSize: theme.typography.body2.fontSize,
                    my: (theme) => `${theme.spacing(3)} !important`,
                  }}
                />
                {/* Data Grid */}
                <Grid container spacing={5}>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      width: '100%',
                      overflowX: 'auto',
                      '& .MuiDataGrid-root': {
                        minWidth: { xs: '800px', sm: '100%' }
                      }
                    }}>
                      <DataGridPro
                        rows={filteredLogs}
                        columns={columnsComLog.map(column => ({
                          ...column,
                          flex: 1,
                          minWidth: 150,
                          headerClassName: 'data-grid-header'
                        }))}
                        sx={{
                          minHeight: 350,
                          maxHeight: 500,
                          "& .MuiDataGrid-columnHeaderTitle": {
                            textTransform: "none",
                            fontSize: { xs: '0.75rem', sm: '0.8125rem' }
                          },
                          "& .MuiDataGrid-cell": {
                            fontSize: { xs: '0.75rem', sm: '0.8125rem' }
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </TabPanel>
              {/* <TabPanel value='8'>
                <Grid container spacing={5}>
                  <Grid item xs={12}>
                    <DataGrid 
                      pagination
                      autoHeight
                      rowSelection={false}
                      columns={columnsHistory} 
                      rows={dummyDataHistory} 
                      rowHeight={62}
                      pageSizeOptions={[5, 10]}
                      paginationModel={paginationModelHistory}
                      onPaginationModelChange={setPaginationModelHistory}
                      sx={{
                        "& .MuiDataGrid-columnHeaderTitle": {
                          textTransform: "none",
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </TabPanel> */}
              <TabPanel value="9">
                <Box sx={{ 
                  display: "flex", 
                  flexDirection: { xs: 'column', sm: 'row' },  // Stack vertically on mobile
                  justifyContent: "flex-end", 
                  alignItems: { xs: 'stretch', sm: 'center' }, // Full width stretch on mobile
                  gap: 2,
                  width: '100%'
                }}>
                    <Button
                      variant="outlined"
                      color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
                      onClick={handleOpenInviteDialog}
                      sx={{ 
                        width: { xs: '100%', md: 'max-content' }, // Full width on mobile
                        order: 1 
                      }}
                      size={ smBelow ? "small" : 'medium' }
                      startIcon={
                        <Box sx={{color: 'success.main',
                          display: 'flex',
                          alignItems: 'center',
                          m: 0,
                          p: 0,}}>
                          <Icon icon='tabler:send' />
                        </Box>
                      }
                    >
                      Send Invites
                    </Button>
                    <Button
                      variant="outlined"
                      color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
                      onClick={handleOpenCustomerDeactivateDlg}
                      sx={{ 
                        width: { xs: '100%', md: 'max-content' }, // Full width on mobile
                        order: 2 // Explicit ordering
                      }}
                      size={ smBelow ? "small" : 'medium' }
                      startIcon={
                        <Box sx={{color: 'error.main',
                          display: 'flex',
                          alignItems: 'center',
                          m: 0,
                          p: 0,}}>
                          <Icon icon='tabler:ban' />
                        </Box>
                      }
                    >
                      Deactivate Portal
                    </Button>
                </Box>
                <Divider
                  sx={{
                    color: "text.disabled",
                    "& .MuiDivider-wrapper": { px: 6 },
                    fontSize: theme.typography.body2.fontSize,
                    my: (theme) => `${theme.spacing(3)} !important`,
                  }}
                ></Divider>
                { customerPortalIsEmpty && (
                  <Box sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    transform: 'translate(0%, 50%)',
                    gap: 2,
                   }}>
                    <img src='/images/CustomerPortalEmptyState.png' />
                    <Typography variant='h6'>No portal activity yet</Typography>
                    <Typography variant='body1'>Send Invites to contacts, to see Records and Activities here under Customer Portal activity.</Typography>
                  </Box>
                )}
                { !customerPortalIsEmpty && (
                  <Box sx={{ height: 500 }}>
                    <DataGridPro columns={columns} rows={dummyData} 
                      // pageSizeOptions={[ 5, 10, { value: -1, label: 'Show All'} ]}
                    />
                  </Box>
                )}
              </TabPanel>
            </TabContext>
          </Box>
        </CardContent>
        <CardActions sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', flexDirection: 'row'}}>
            {tabValue !== '4' && (
              <Button sx={{position: 'absolute', bottom: 20, right: 20}} variant="contained" color="main"  onClick={() => handleSave()} disabled={btnLoading || !isChanged} >
                  {btnLoading ? <CircularProgress size={24} /> : 'Save Changes'}
              </Button>
            )}
            {tabValue === '4' && (
              <Button sx={{position: 'absolute', bottom: 20, right: 20}} onClick={handlePageBack} variant="contained" color="main">
                Return to List
              </Button>
            )}
          </CardActions>
        {quickViewOpen && (
        <QuickView
          handleQuickViewClose = {handleQuickViewClose }           
          jobsite={jobsite}
          contacts={allContacts}
          attachments={attachments}
          propertyAreaList={propertyAreaList} 
          alertList = {alertList}
          setAlertList = {setAlertList}   
        />
        )}
      </Card>

      {/* Send Portal Invite Sent Toast */}
      {/*<ToastContainer />*/}

      {/* Add Contact Dialog */}
      <AddContactDialog 
        open={openNewContactDlg}
        handleClose={handleCloseNewContactDlg}
        fetchContactData = {fetchContactData}
        customer = {customer}
        toast = {toast}
        setLoading={setLoading}        
        contacts = {allContacts}
      />

      <UpdateContactDialog 
        open={openUpdateContactDlg}
        handleClose={handleCloseUpdateDlg}
        customer = {customer}
        contactID = {contactID}
        fetchContactData = {fetchContactData}
        setContactID = {setContactID}
        setLoading= {setLoading}
        contacts = {allContacts}
         onPhoneUpdate={(newPhone) => {
    setCustomer(prev => ({
      ...prev,
      phoneNumbers: [{ phoneNo: newPhone, isPrimary: true }]
    }));
  }}
      />

      {/* Add Jobsite Dialog */}
      <AddJobsiteDialog 
        open={openNewJobsiteDlg}
        setJobsiteOpen={setOpenNewJobsiteDlg}
        handleClose={handleCloseNewJobsiteDlg}
        customer = {customer}
        fetchContactData = {fetchContactData}
        toast = {toast}
        jobsite = {jobsite}
        handleJobsiteChange = {handleJobsiteChange}      
        navigate = {navigate} 
        NProgress = {NProgress}
        fetchContacts = {fetchContacts}  
        contacts = {allContacts}    
        propertyArea = {propertyArea}
        setPropertyArea = {setPropertyArea}
        areaMeasurement = {areaMeasurement} 
        setAreaMeasurement = {setAreaMeasurement}
        setLoading={setLoading}
        fetchJobsites={fetchJobsites}
        activeStep={activeStepJobsite}
        setActiveStep={setActiveStepJobsite}
        clearAreaMeasurement={clearAreaMeasurement}
        fetchPropertyArea = {fetchPropertyArea}
        propertyAreaList = {propertyAreaList}
        clearPropertyArea = {clearPropertyArea}
        jobsiteContent = {jobsiteContent} 
        alertList = {alertList}
        setAlertList = {setAlertList}        
        files = {files}
        setFiles = {setFiles}
      /> 
      <AttachmentUpload
        open={openUploadDlg}                
        handleClose={handleCloseUploadDlg}  
        Title="Upload Attachment"
        files = {uploadFiles}
        btnLoader = {btnLoader}
        setUploadFiles={setUploadFiles}
        setFiles = {setUploadFiles}
        setBtnLoader = {setBtnLoader}
        navigate = {navigate}
        entity='customer'
        payload = {customer}
        setOpenUploadDlg = {setOpenUploadDlg}  
        fetchAttachments = {fetchAttachments}
      />  
      {/* Add Communication Log */}
      <AddCommunicationLogDialog 
        open={openNewComLogDlg}
        handleClose={handleCloseNewComLogDlg}
        customerID={customerID}
        fetchLogs={() => {fetchLogs(null, null, 1, 10000)}}
        logData={selectedLog}
        date={comLogDialogDate}
        contacts = {allContacts}        
      />

      <BulkImport
        open={openBulkImportDlg}                
        handleClose={handleCloseBulkImportDlg}
        content={BulkImportContent}  
        files = {uploadFiles}
        loading = {btnLoader}
        getRootProps =  {getRootProps}
        getInputProps = {getInputProps}
        setFiles = {setUploadFiles}
        setLoading = {setLoading}
        navigate = {navigate}     
        fetchContactData = {fetchContacts}
        fetchJobsiteData = {fetchJobsites}         
      />  

      {/* Send Portal Invite Dialog */}
      <SendPortalInviteDialog 
        open={openInviteDialog}
        handleClose={handleCloseInviteDialog}
        handleSendInvite={handleSendInvite}
      />
      
        
      {/* Deactivate Customer Dialog */}
      <DeactivateCustomerDialog 
        open={openDeactiveCustomerDlg}
        handleClose={handleCloseCustomerDeactivateDlg}
      />
      
      
       
      

      <UnsavedChangesDialog open={openUnsavedChangesDlg} handleClose={handleCloseUnsavedChangesDlg} handleDiscard={handleDiscardChanges} />

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 10000 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </UserLayout>
  );
};

export default CustomerDetail;
